import axios from 'axios';
import * as Sentry from '@sentry/react';
import { Fair, FairStats, Product, ProductCreate, ProductUpdate, ProductListResponse, ProductWithFair, User, UserProfile, UserProfileUpdate, UserFavorite, Review, ReviewCreate, ReviewUpdate, FairImage, FairImageCreate, FairImageUpdate, PaginatedResponse, Slot, SlotCreate, SlotUpdate, SlotFilters, SlotListResponse, SlotWithReservationListResponse, SlotAvailabilitySummary, SlotComparisonResponse, SlotAvailabilityMatrix, SlotResponse, SlotAvailabilityResponse, BulkSlotCreate, BulkSlotCreateResponse, BoothNeighbor, Vendor, VendorCreate, VendorUpdate, VendorPublicProfile, VendorListResponse, AdminVendorListResponse, PortfolioImageUploadResponse, VendorPublicProductItem, VendorUpcomingFair, VendorReservation, FairVendorResponse, FairProductResponse, Reservation, ReservationCreate, ReservationDetail, ReservationListResponse, ReservationStatusUpdate, SlotAvailabilityCheck, WaitlistEntry, WaitlistDetailResponse, WaitlistPositionResponse, WaitlistStatsResponse, WaitlistAnalyticsResponse, WaitlistListResponse, WaitlistJoinRequest, RentalItem, RentalItemCreate, RentalItemUpdate, RentalItemListResponse, ReservationRentalItem, ReservationRentalItemCreate, RentalStats, ProductCategoryResponse, FeatureFlag, FeatureFlagCreate, FeatureFlagUpdate, FeatureFlagStatusResponse, FeatureFlagListResponse, FairManagerRequestCreate, FairManagerRequestResponse, FairManagerRequestListResponse, FairManagerDashboardStats, FairManagerPayoutsResponse, FairSubmissionCheckoutResponse, FairSubmissionStatusResponse, CalendarMonthResponse, FairInquiryCreate, FairInquiryResponse, FairInquiryDetailResponse, FairInquiryListResponse, InquiryMessageCreate, InquiryMessageResponse, InquiryStatusUpdate, DiscountCode, DiscountCodeCreate, DiscountCodeUpdate, DiscountCodeListResponse, DiscountCodeValidateRequest, DiscountCodeValidateResponse, DiscountCodeAnalytics, FairVendorProduct, FairVendorProductCreate, FairVendorProductUpdate, FairVendorProductListResponse, VendorBoothPreferences, VendorBoothPreferencesUpdate, BoothMatchResult, BoothMatchRequest, SlotPricingTemplate, SlotPricingTemplateCreate, SlotPricingTemplateUpdate, SlotPricingTemplateListResponse, SlotPriceOverrideRequest, NotificationListResponse, NotificationSnoozeRequest, NotificationSnoozeResponse, NotificationPreferences, NotificationPreferencesUpdate, AdPackage, AdPackageCreate, AdPackageUpdate, AdPackageListResponse, AdPackageAnalytics, AdPurchase, AdPurchaseCreate, AdPurchaseStatusUpdate, AdPurchaseListResponse, AdType, Advertisement, AdvertisementCreate, AdvertisementUpdate, VendorAdvertisementUpdate, AdvertisementAnalytics, AdvertisementListResponse, EmailCampaignCreate, EmailCampaignResponse, EmailCampaignListResponse, EmailCampaignAnalytics, VendorFairExpense, VendorFairExpenseCreate, VendorFairExpenseUpdate, VendorFairRevenue, VendorFairRevenueCreate, VendorFairRevenueUpdate, FairROICalculation, VendorLifetimeROI, PaginatedExpenseResponse, PaginatedRevenueResponse, VendorFairMetricsCreate, VendorFairMetricsUpdate, VendorFairMetricsResponse, VendorSatisfactionTrend, FairManagerFeedbackSummary, ProductPreOrder, ProductPreOrderCreate, ProductPreOrderUpdate, ProductPreOrderListResponse, MarkPickedUpRequest, PreOrderStatus, PreOrderPaymentStatus, Role, Permission, RoleAnalytics, UsersWithRolesResponse, UserRoleAssignRequest, UserRoleResponse, SetUserRolesRequest, SetUserRolesResponse, AdminReservationResponse, AdminReservationListResponse, AdminReservationUpdate, ProfileViewsAnalyticsResponse, SearchPerformanceResponse, ConversionFunnelResponse, DiscoverySourcesResponse, ProductRanking, FairComparisonData, SeasonalInsight, VendorReview, VendorReviewCreate, VendorReviewUpdate, VendorReviewListResponse, VendorReviewAdminUpdate, VendorRatingSummary, VendorReviewEligibility, VendorBadge, VendorBadgeCreate, VendorBadgeUpdate, VendorBadgeAward, VendorBadgeProgress, VendorBadgeListResponse, BoothLayout, BoothLayoutCreate, BoothLayoutUpdate, BoothLayoutBulkUpload, BoothLayoutBulkUploadResponse, BoothAmenity, BoothAmenityCreate, BoothAmenityUpdate, BoothDistance, ContactSettingsResponse, ContactSettingsUpdate, BoothMapData, BlogCategory, BlogCategoryCreate, BlogTag, BlogTagCreate, BlogPost, BlogPostCreate, BlogPostUpdate, BlogComment, BlogCommentCreate, BlogPostListParams, OnboardingStatusResponse, OnboardingProgressSummary } from '../types';
import { FairManagerApplicationResponse, FairManagerApplicationUpdate } from '../types/generated';
import { extractApiError } from '../utils/errorUtils';
import { getStateFromHostname } from '../utils/geofencing';

// Handle environment variables in both Jest and Vite environments
const getEnvVar = (key: string, defaultValue: string) => {
  // In Jest/Node environment, use process.env
  if (typeof process !== 'undefined' && process.env) {
    const value = process.env[key];
    if (value) return value;
  }

  // In Vite/browser environment, check for Vite-injected env
  // Note: Vite replaces import.meta.env.* at build time, so we can't access it dynamically
  // For Vite environment, rely on build-time replacement or process.env fallback

  return defaultValue;
};

// Environment-aware API URL configuration
const getApiBaseUrl = () => {
  // Check if we have an explicit environment variable
  const envUrl = getEnvVar('VITE_API_BASE_URL', '');
  if (envUrl) {
    return envUrl;
  }

  // Auto-detect environment based on hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const port = window.location.port;

    // Local development (all ports) - direct backend access
    // Both Docker (8080) and Vite (5173) use direct backend URL
    if (hostname === 'localhost' && (port === '8080' || port === '5173' || port === '3000')) {
      return 'http://localhost:8000';
    }

    // Production domain
    if (hostname === 'localartistfairs.com' || hostname === 'www.localartistfairs.com') {
      return 'https://api.localartistfairs.com';
    }

    // Fly.dev staging/preview
    if (hostname.includes('fly.dev')) {
      return 'https://localartistfairs-api.fly.dev';
    }
  }

  // Default fallback to Fly.dev
  return 'https://localartistfairs-api.fly.dev';
};

export const API_BASE_URL = getApiBaseUrl();

// Create axios instance with default config
// Note: API key removed for security - backend validates via JWT tokens for authenticated endpoints
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with cross-origin requests
});

// Export api instance for components that need direct access (e.g., PreLaunchPage Issue #197)
export { api };

// State for managing refresh token flow
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: string | null) => void;
  reject: (reason?: Error) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error as Error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add auth token and CSRF token interceptor
api.interceptors.request.use((config) => {
  // Add access token
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add CSRF token for state-changing requests
  // Note: Cookie is HttpOnly (secure) but can't be read by JS, so we use localStorage
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase() || '')) {
    const csrfToken = localStorage.getItem('csrf_token');
    if (import.meta.env.DEV) {
      console.log(`[Interceptor] ${config.method} ${config.url} - CSRF token:`, csrfToken ? `${csrfToken.substring(0, 20)}...` : 'NOT FOUND');
    }
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
      if (import.meta.env.DEV) {
        console.log('[Interceptor] Added X-CSRF-Token header');
      }
    } else {
      console.warn('[Interceptor] WARNING: No CSRF token found in localStorage for state-changing request!');
    }
  }

  return config;
});

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't retried yet, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't intercept login or password reset requests - let them fail naturally
      const url = originalRequest.url || '';
      const fullUrl = originalRequest.baseURL ? originalRequest.baseURL + url : url;
      if (import.meta.env.DEV) {
        console.log('[Interceptor] 401 error on URL:', fullUrl);
      }

      if (url.includes('/auth/login') || fullUrl.includes('/auth/login') ||
          url.includes('/auth/request-reset') || url.includes('/auth/reset-password')) {
        if (import.meta.env.DEV) {
          console.log('[Interceptor] Skipping redirect for auth endpoint');
        }
        return Promise.reject(error);
      }

      if (import.meta.env.DEV) {
        console.log('[Interceptor] Attempting token refresh or redirect');
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        // No refresh token, redirect to appropriate login page
        const currentPath = window.location.pathname;
        if (import.meta.env.DEV) {
          console.log('[Interceptor] No refresh token. Current path:', currentPath);
        }
        authApi.logout();
        // Check if current path is admin-related
        const isAdminPath = currentPath.startsWith('/admin');
        const redirectUrl = isAdminPath ? '/admin/login' : '/login';
        if (import.meta.env.DEV) {
          console.log('[Interceptor] Redirecting to:', redirectUrl);
        }
        window.location.href = redirectUrl;
        return Promise.reject(error);
      }

      try {
        // Try to refresh the access token
        const response = await api.post('/auth/refresh', {
          refresh_token: refreshToken
        });

        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);

        // Update authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        processQueue(null, access_token);
        isRefreshing = false;

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        // Refresh failed, logout and redirect to appropriate login page
        const currentPath = window.location.pathname;
        if (import.meta.env.DEV) {
          console.log('[Interceptor] Refresh failed. Current path:', currentPath);
        }
        authApi.logout();
        // Check if current path is admin-related
        const isAdminPath = currentPath.startsWith('/admin');
        const redirectUrl = isAdminPath ? '/admin/login' : '/login';
        if (import.meta.env.DEV) {
          console.log('[Interceptor] Redirecting to:', redirectUrl);
        }
        window.location.href = redirectUrl;
        return Promise.reject(refreshError);
      }
    }

    // Handle CSRF validation failures (Issue #77)
    if (error.response?.status === 403 && error.response?.data?.detail === 'CSRF validation failed') {
      if (import.meta.env.DEV) {
        console.log('[Interceptor] CSRF validation failed, refreshing token');
      }
      try {
        // Refresh CSRF token
        const response = await api.get('/csrf-token');
        if (response.data && response.data.csrf_token) {
          localStorage.setItem('csrf_token', response.data.csrf_token);
          // Retry the original request
          return api(originalRequest);
        }
      } catch (csrfError) {
        console.error('[Interceptor] Failed to refresh CSRF token:', csrfError);
        return Promise.reject(csrfError);
      }
    }

    return Promise.reject(error);
  }
);

// Fetch CSRF token on app initialization
export async function initCsrfToken(): Promise<void> {
  try {
    const response = await api.get('/csrf-token');
    // Store the CSRF token from response body (cookie is HttpOnly and can't be read by JS)
    if (response.data && response.data.csrf_token) {
      localStorage.setItem('csrf_token', response.data.csrf_token);
    }
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
  }
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  refresh_token?: string;
  user: {
    id: number;  // Issue #911: Added missing id field
    email: string;
    // Issue #865: RBAC replaces legacy boolean flags
    roles: string[];  // e.g., ["Admin", "Vendor"]
    permissions: Record<string, boolean>;  // e.g., {"manage_fairs": true}
  };
}

export interface SearchParams {
  state?: string;
  city?: string;
  product_category?: string;
}

export const authApi = {
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await api.post('/auth/login', { email, password });
    const data = response.data;

    // Store tokens in localStorage
    localStorage.setItem('access_token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }
    localStorage.setItem('user_id', data.user.id.toString());
    localStorage.setItem('user_email', data.user.email);

    // Issue #860: RBAC Migration - Store full user object with roles
    localStorage.setItem('user', JSON.stringify(data.user));

    // Legacy flag storage removed in Phase 4 (Issue #865)
    // All role checking now uses RBAC utilities in src/utils/rbac.ts

    // Issue #1128: Set Sentry user context for error tracking
    Sentry.setUser({
      id: data.user.id.toString(),
      email: data.user.email,
      roles: data.user.roles.join(', '),
    });

    // Fetch fresh CSRF token after login
    await initCsrfToken();

    return data;
  },

  async getMe() {
    const response = await api.get('/auth/me');
    const userData = response.data;

    // Issue #911: Update localStorage with fresh user data including roles/permissions
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));

      // Issue #1128: Update Sentry user context
      Sentry.setUser({
        id: userData.id?.toString(),
        email: userData.email,
        roles: userData.roles?.join(', '),
      });
    }

    return userData;
  },

  async getCurrentUser() {
    const response = await api.get('/auth/me');
    const userData = response.data;

    // Issue #911: Update localStorage with fresh user data including roles/permissions
    // This ensures AuthGuards have up-to-date role information after token verification
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));

      // Issue #1128: Update Sentry user context
      Sentry.setUser({
        id: userData.id?.toString(),
        email: userData.email,
        roles: userData.roles?.join(', '),
      });
    }

    return userData;
  },

  async logout(revokeToken: boolean = true) {
    // Optionally revoke the refresh token on the server
    if (revokeToken) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          await api.post('/auth/revoke', { refresh_token: refreshToken });
        } catch (error) {
          console.error('Failed to revoke refresh token:', error);
        }
      }
    }

    // Issue #1128: Clear Sentry user context on logout
    Sentry.setUser(null);

    // Clear local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_email');
    // Legacy flag cleanup removed in Phase 4 (Issue #865)
    localStorage.removeItem('user');
    localStorage.removeItem('csrf_token'); // Clear CSRF token on logout
  },

  async revokeAllTokens() {
    try {
      await api.post('/auth/revoke-all');
      this.logout(false); // Don't try to revoke again
    } catch (error) {
      console.error('Failed to revoke all tokens:', error);
      throw error;
    }
  },

  isLoggedIn(): boolean {
    return !!localStorage.getItem('access_token');
  },

  // Issue #860: Use RBAC roles instead of legacy boolean flags
  isAdmin(): boolean {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;
    try {
      const user = JSON.parse(userStr);
      return user.roles?.includes('Admin') || false;
    } catch {
      return false;
    }
  },

  // Issue #860: Use RBAC roles instead of legacy boolean flags
  isFairManager(): boolean {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;
    try {
      const user = JSON.parse(userStr);
      // Admins automatically have Fair Manager permissions
      return user.roles?.includes('Fair Manager') || user.roles?.includes('Admin') || false;
    } catch {
      return false;
    }
  },

  async changePassword(currentPassword: string, newPassword: string, confirmPassword: string) {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword
    });
    return response.data;
  },

  async requestPasswordReset(email: string) {
    const response = await api.post('/auth/request-reset', { email });
    return response.data;
  },

  async resetPassword(token: string, newPassword: string, confirmPassword: string) {
    const response = await api.post('/auth/reset-password', {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword
    });
    return response.data;
  },

  async verifyEmail(token: string) {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  async resendVerification(email: string) {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },
};

export const fairApi = {
  async getAll(params?: SearchParams): Promise<Fair[]> {
    // Auto-inject state parameter from hostname for geofencing (Issue #1332)
    const mergedParams = { ...params };
    if (!mergedParams.state) {
      const stateFromDomain = getStateFromHostname();
      if (stateFromDomain) {
        mergedParams.state = stateFromDomain;
      }
    }

    const response = await api.get('/fairs', { params: mergedParams });
    // API now returns paginated data: {items, total, page, page_size, total_pages}
    // Extract the items array for backward compatibility
    return response.data.items || response.data;
  },

  async getAllPaginated(params?: SearchParams & { page?: number; page_size?: number }): Promise<PaginatedResponse<Fair>> {
    // Auto-inject state parameter from hostname for geofencing (Issue #1332)
    const mergedParams = { ...params };
    if (!mergedParams.state) {
      const stateFromDomain = getStateFromHostname();
      if (stateFromDomain) {
        mergedParams.state = stateFromDomain;
      }
    }

    const response = await api.get('/fairs', { params: mergedParams });
    return response.data;
  },

  async getById(
    id: number,
    options?: {
      include_availability?: boolean;
      include_vendor_stats?: boolean;
    }
  ): Promise<Fair> {
    const response = await api.get(`/fairs/${id}`, { params: options });
    return response.data;
  },

  async search(state?: string, city?: string, productCategory?: string): Promise<Fair[]> {
    const params: SearchParams = {};
    if (state) params.state = state;
    if (city) params.city = city;
    if (productCategory) params.product_category = productCategory;

    return this.getAll(params);
  },

  async searchPaginated(
    state?: string,
    city?: string,
    productCategory?: string,
    page?: number,
    pageSize?: number,
    startDate?: string,
    endDate?: string
  ): Promise<PaginatedResponse<Fair>> {
    const params: SearchParams & { page?: number; page_size?: number; start_date?: string; end_date?: string } = {};
    if (state) params.state = state;
    if (city) params.city = city;
    if (productCategory) params.product_category = productCategory;
    if (page) params.page = page;
    if (pageSize) params.page_size = pageSize;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    return this.getAllPaginated(params);
  },

  // Calendar view (Issue #211)
  async getCalendarMonth(year: number, month: number, includeTest: boolean = false, excludeDrafts: boolean = true): Promise<CalendarMonthResponse> {
    const response = await api.get('/fairs/calendar', {
      params: {
        year,
        month,
        include_test: includeTest,
        exclude_drafts: excludeDrafts
      }
    });
    return response.data;
  },

  async getFairVendors(fairId: number): Promise<VendorPublicProfile[]> {
    const response = await api.get(`/fairs/${fairId}/vendors`);
    return response.data;
  },

  async getFairProducts(fairId: number): Promise<Product[]> {
    const response = await api.get(`/fairs/${fairId}/products`);
    return response.data;
  },

  async getBoothMap(fairId: number): Promise<BoothMapData> {
    // Issue #1078: Get complete booth map data including layouts, amenities, and slots
    // Public endpoint - no authentication required
    // Feature flag checked by backend endpoint
    const response = await api.get(`/fairs/${fairId}/booth-map`);
    return response.data;
  },
};

export const adminApi = {
  async getAllFairs(params?: SearchParams): Promise<Fair[]> {
    const response = await api.get('/admin/fairs', { params });
    // API returns paginated data - extract items array
    return response.data.items || response.data;
  },

  async getFairById(id: number): Promise<Fair> {
    const response = await api.get(`/admin/fairs/${id}`);
    return response.data;
  },

  async createFair(fair: Omit<Fair, 'id'>): Promise<Fair> {
    const response = await api.post('/admin/fairs', fair);
    return response.data;
  },

  async updateFair(id: number, updates: Partial<Fair>): Promise<Fair> {
    const response = await api.put(`/admin/fairs/${id}`, updates);
    return response.data;
  },

  async deleteFair(id: number): Promise<void> {
    await api.delete(`/admin/fairs/${id}`);
  },

  async importFairs(formData: FormData): Promise<{total: number; created: number; skipped: number; errors: {row: number; field: string; message: string}[]}> {
    // Issue #1431: Bulk import fairs from CSV, Excel, or Google Sheets
    const response = await api.post('/admin/fairs/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async restoreFair(id: number): Promise<void> {
    await api.post(`/admin/fairs/${id}/restore`);
  },

  async addProduct(fairId: number, product: Omit<Product, 'id'>): Promise<Product> {
    const response = await api.post(`/admin/fairs/${fairId}/products`, product);
    return response.data;
  },

  async createProduct(product: Omit<Product, 'id'>): Promise<Product> {
    const response = await api.post('/admin/products', product);
    return response.data;
  },

  async updateProduct(productId: number, updates: Partial<Product>): Promise<Product> {
    const response = await api.put(`/admin/products/${productId}`, updates);
    return response.data;
  },

  async deleteProduct(productId: number): Promise<void> {
    await api.delete(`/admin/products/${productId}`);
  },

  async restoreProduct(productId: number): Promise<void> {
    await api.post(`/admin/products/${productId}/restore`);
  },

  // Legacy artist fair-specific product methods (kept for compatibility)
  async updateProductInFair(fairId: number, productId: number, updates: Partial<Product>): Promise<Product> {
    const response = await api.put(`/admin/fairs/${fairId}/products/${productId}`, updates);
    return response.data;
  },

  async deleteProductFromFair(fairId: number, productId: number): Promise<void> {
    await api.delete(`/admin/fairs/${fairId}/products/${productId}`);
  },

  async getAllProducts(page: number = 1, pageSize: number = 50): Promise<PaginatedResponse<ProductWithFair>> {
    const response = await api.get('/admin/products', {
      params: { page, page_size: pageSize }
    });
    return response.data;
  },

  async getFairProducts(fairId: number): Promise<FairProductResponse[]> {
    // Issue #80: Now uses correct relationship chain: Fair → Slot → Reservation → Vendor → Product
    // Returns products from vendors with confirmed reservations at this fair
    const response = await api.get(`/fairs/${fairId}/products`);
    return response.data;
  },

  async getFairVendors(fairId: number, status: string = 'confirmed'): Promise<FairVendorResponse[]> {
    // Issue #80: Get vendors with reservations at a fair
    // Fair → Slot → Reservation → Vendor relationship chain
    const response = await api.get(`/fairs/${fairId}/vendors`, {
      params: { status }
    });
    return response.data;
  },

  // User management methods
  async getAllUsers(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    email_verified?: boolean;
  }): Promise<PaginatedResponse<User> | User[]> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.email_verified !== undefined) queryParams.append('email_verified', params.email_verified.toString());

    const response = await api.get(`/admin/users?${queryParams.toString()}`);

    // Backward compatibility: if called without params, return array for legacy callers
    // If called with params (new behavior), return full paginated response
    if (!params) {
      return response.data.items || response.data;
    }
    return response.data;
  },

  async createUser(userData: {
    email: string;
    password: string;
    // Issue #865: Legacy is_admin removed.
    // To assign Admin role, use POST /api/roles/assign after user creation
  }): Promise<User> {
    const response = await api.post('/admin/users', userData);
    return response.data;
  },

  async getUserById(userId: number): Promise<User> {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  },

  async updateUser(userId: number, updates: {
    email?: string;
    password?: string;
    // Issue #865: Legacy is_admin removed.
    // To assign Admin role, use POST /api/roles/assign
  }): Promise<User> {
    const response = await api.put(`/admin/users/${userId}`, updates);
    return response.data;
  },

  async deleteUser(userId: number): Promise<void> {
    await api.delete(`/admin/users/${userId}`);
  },

  async verifyUserEmail(userId: number): Promise<{ message: string; user_id: number }> {
    const response = await api.post(`/admin/users/${userId}/verify-email`);
    return response.data;
  },

  async getProductAvailabilities(): Promise<Array<{id: number, name: string, display_name: string, display_order: number}>> {
    const response = await api.get('/product-availabilities');
    return response.data;
  },

  // Artist Fair Image management methods
  async getFairImages(fairId: number): Promise<FairImage[]> {
    const response = await api.get(`/fairs/${fairId}/images`);
    return response.data;
  },

  async createFairImage(fairId: number, imageData: FairImageCreate): Promise<FairImage> {
    const response = await api.post(`/admin/fairs/${fairId}/images`, imageData);
    return response.data;
  },

  async updateFairImage(fairId: number, imageId: number, imageData: FairImageUpdate): Promise<FairImage> {
    const response = await api.put(`/admin/fairs/${fairId}/images/${imageId}`, imageData);
    return response.data;
  },

  async deleteFairImage(fairId: number, imageId: number): Promise<void> {
    await api.delete(`/admin/fairs/${fairId}/images/${imageId}`);
  },

  async reorderFairImages(fairId: number, imageIds: number[]): Promise<{message: string}> {
    const response = await api.put(`/admin/fairs/${fairId}/images/reorder`, imageIds);
    return response.data;
  },

  // Pending Artist Fair Submissions
  async getPendingFairs(params?: { status_filter?: string; page?: number; page_size?: number }): Promise<PaginatedResponse<unknown>> {
    const response = await api.get('/admin/pending-fairs', { params });
    return response.data;
  },

  async getPendingFairById(pendingId: number): Promise<unknown> {
    const response = await api.get(`/admin/pending-fairs/${pendingId}`);
    return response.data;
  },

  async approvePendingFair(pendingId: number, data: { email?: string; phone?: string; approved_product_ids: number[] }): Promise<unknown> {
    const response = await api.put(`/admin/pending-fairs/${pendingId}/approve`, data);
    return response.data;
  },

  async rejectPendingFair(pendingId: number, data: { rejection_reason: string }): Promise<unknown> {
    const response = await api.put(`/admin/pending-fairs/${pendingId}/reject`, data);
    return response.data;
  },

  // Convenience method for backward compatibility with components using adminApi.get()
  async get<T = unknown>(url: string, config?: Record<string, unknown>): Promise<{ data: T }> {
    const response = await api.get(`/admin${url}`, config);
    return response;
  },

  async put<T = unknown>(url: string, data?: unknown, config?: Record<string, unknown>): Promise<{ data: T }> {
    const response = await api.put(`/admin${url}`, data, config);
    return response;
  },

  // Product verification methods
  async approveProduct(productId: number): Promise<void> {
    await api.post(`/admin/products/${productId}/approve`);
  },

  async rejectProduct(productId: number): Promise<void> {
    await api.post(`/admin/products/${productId}/reject`);
  },

  // Analytics API (Issue #630)
  async getWaitlistAnalytics(params?: {
    start_date?: string;
    end_date?: string;
    fair_id?: number;
  }): Promise<WaitlistAnalyticsResponse> {
    const response = await api.get('/admin/analytics/waitlist', { params });
    return response.data;
  },

  // Fair Manager Applications API (Issue #1477)
  async getAllManagerApplications(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: FairManagerApplicationResponse[]; total: number }> {
    const response = await api.get('/admin/manager-applications', { params });
    return response.data;
  },

  async getManagerApplicationById(id: number): Promise<FairManagerApplicationResponse> {
    const response = await api.get(`/admin/manager-applications/${id}`);
    return response.data;
  },

  async reviewManagerApplication(
    id: number,
    data: FairManagerApplicationUpdate
  ): Promise<FairManagerApplicationResponse> {
    const response = await api.patch(`/admin/manager-applications/${id}`, data);
    return response.data;
  },
};

// Fair Manager API - requires Fair Manager role via RBAC (Issue #146, Issue #865)
export const fairManagerApi = {
  async getFairs(): Promise<Fair[]> {
    const response = await api.get('/fairmanager/fairs');
    return response.data;
  },

  async getFairById(id: number): Promise<Fair> {
    const response = await api.get(`/fairmanager/fairs/${id}`);
    return response.data;
  },

  async createFair(fair: Omit<Fair, 'id'>): Promise<Fair> {
    const response = await api.post('/fairmanager/fairs', fair);
    return response.data;
  },

  async updateFair(id: number, updates: Partial<Fair>): Promise<Fair> {
    const response = await api.put(`/fairmanager/fairs/${id}`, updates);
    return response.data;
  },

  async deleteFair(id: number): Promise<void> {
    await api.delete(`/fairmanager/fairs/${id}`);
  },

  async getFairStats(fairId: number): Promise<FairStats> {
    const response = await api.get(`/fairmanager/fairs/${fairId}/stats`);
    return response.data;
  },

  // Issue #379: Cancel fair with automatic bulk refunds
  async cancelFairWithRefunds(
    fairId: number,
    data: { cancellation_reason: string }
  ): Promise<{
    success: boolean;
    refunds_processed: number;
    refunds_failed: number;
    failed_refunds: Array<{
      reservation_id: number;
      error: string;
      vendor_email: string;
    }>;
    reservations_cancelled: number;
    message: string;
  }> {
    const response = await api.post(`/fairmanager/fairs/${fairId}/cancel-with-refunds`, data);
    return response.data;
  },

  // Issue #400: Slot Management with Reservation Details
  async getSlotsWithReservations(
    fairId: number,
    params?: {
      status?: 'available' | 'reserved' | 'pending';
      slot_type?: string;
      page?: number;
      page_size?: number;
    }
  ): Promise<SlotWithReservationListResponse> {
    const response = await api.get(`/fairmanager/fairs/${fairId}/slots`, { params });
    return response.data;
  },

  // Issue #958: Vendor Feedback Dashboard (Issue #682)
  async getFairVendorFeedback(fairId: number): Promise<FairManagerFeedbackSummary> {
    const response = await api.get(`/fairmanager/fairs/${fairId}/vendor-feedback`);
    return response.data;
  },

  // ========== Phase 1: Fair Submission, Email Campaigns, Slot Price Override (Issue #966) ==========

  /**
   * Create Stripe checkout session for fair submission payment
   * Issue #966 - Phase 1: Fair Submission Payment Flow
   */
  createFairSubmissionCheckout: async (fairId: number): Promise<FairSubmissionCheckoutResponse> => {
    const response = await api.post(`/fairmanager/fairs/${fairId}/create-submission-checkout`);
    return response.data;
  },

  /**
   * Get fair submission payment status
   * Issue #966 - Phase 1: Fair Submission Payment Flow
   */
  getFairSubmissionStatus: async (fairId: number): Promise<FairSubmissionStatusResponse> => {
    const response = await api.get(`/fairmanager/fairs/${fairId}/submission-status`);
    return response.data;
  },

  /**
   * Create and send email campaign
   * Issue #966 - Phase 1: Email Campaign Management
   */
  createEmailCampaign: async (fairId: number, campaign: EmailCampaignCreate): Promise<EmailCampaignResponse> => {
    const response = await api.post(`/fairmanager/fairs/${fairId}/email-campaigns`, campaign);
    return response.data;
  },

  /**
   * List email campaigns for a fair (paginated)
   * Issue #966 - Phase 1: Email Campaign Management
   */
  listEmailCampaigns: async (fairId: number, params?: {
    page?: number;
    page_size?: number;
  }): Promise<EmailCampaignListResponse> => {
    const response = await api.get(`/fairmanager/fairs/${fairId}/email-campaigns`, { params });
    return response.data;
  },

  /**
   * Get email campaign details
   * Issue #966 - Phase 1: Email Campaign Management
   */
  getEmailCampaign: async (campaignId: number): Promise<EmailCampaignResponse> => {
    const response = await api.get(`/fairmanager/email-campaigns/${campaignId}`);
    return response.data;
  },

  /**
   * Get email campaign analytics
   * Issue #966 - Phase 1: Email Campaign Management
   */
  getEmailCampaignAnalytics: async (campaignId: number): Promise<EmailCampaignAnalytics> => {
    const response = await api.get(`/fairmanager/email-campaigns/${campaignId}/analytics`);
    return response.data;
  },

  /**
   * Set or clear individual slot price override
   * Issue #966 - Phase 1: Slot Price Override
   * Issue #890: Uses string for Decimal precision
   * @param priceOverride - String price to set, or null to clear the override
   */
  setSlotPriceOverride: async (slotId: number, priceOverride: string | null): Promise<Slot> => {
    const response = await api.put(`/fairmanager/slots/${slotId}/price-override`, {
      price_override: priceOverride
    });
    return response.data;
  },

  // ========== Phase 2: Booth Layout & Amenities Management (Issue #966) ==========

  /**
   * Bulk upload booth layouts for multiple slots
   * Issue #966 - Phase 2: Booth Layout Management (Issue #676)
   */
  bulkUploadBoothLayouts: async (fairId: number, data: BoothLayoutBulkUpload): Promise<BoothLayoutBulkUploadResponse> => {
    const response = await api.post(`/fairmanager/fairs/${fairId}/booth-layouts/bulk`, data);
    return response.data;
  },

  /**
   * Create individual booth layout
   * Issue #952 - Complete booth map CRUD operations
   */
  createBoothLayout: async (fairId: number, data: Omit<BoothLayoutCreate, 'fair_id'>): Promise<BoothLayout> => {
    const response = await api.post(`/fairmanager/fairs/${fairId}/booth-layouts`, {
      fair_id: fairId,
      ...data
    });
    return response.data;
  },

  /**
   * Update booth layout for a specific slot
   * Issue #966 - Phase 2: Booth Layout Management (Issue #676)
   */
  updateBoothLayout: async (fairId: number, slotId: number, data: BoothLayoutUpdate): Promise<BoothLayout> => {
    const response = await api.put(`/fairmanager/fairs/${fairId}/booth-layouts/${slotId}`, data);
    return response.data;
  },

  /**
   * Delete booth layout by ID
   * Issue #952 - Complete booth map CRUD operations
   */
  deleteBoothLayout: async (layoutId: number): Promise<void> => {
    await api.delete(`/fairmanager/booth-layouts/${layoutId}`);
  },

  /**
   * Create booth amenity for a fair
   * Issue #966 - Phase 2: Booth Amenities Management (Issue #676, Issue #760, Issue #893)
   * Note: fair_id is injected by wrapper, callers should omit it
   */
  createBoothAmenity: async (fairId: number, data: Omit<BoothAmenityCreate, 'fair_id'>): Promise<BoothAmenity> => {
    const response = await api.post(`/fairmanager/fairs/${fairId}/amenities`, {
      fair_id: fairId,
      ...data
    });
    return response.data;
  },

  /**
   * Update booth amenity
   * Issue #966 - Phase 2: Booth Amenities Management (Issue #676, Issue #760)
   */
  updateBoothAmenity: async (amenityId: number, data: BoothAmenityUpdate): Promise<BoothAmenity> => {
    const response = await api.put(`/fairmanager/amenities/${amenityId}`, data);
    return response.data;
  },

  /**
   * Delete booth amenity
   * Issue #966 - Phase 2: Booth Amenities Management (Issue #676, Issue #760)
   */
  deleteBoothAmenity: async (amenityId: number): Promise<void> => {
    await api.delete(`/fairmanager/amenities/${amenityId}`);
  },

  /**
   * Get booth layouts for a fair
   * Issue #1028 - Phase 2B: Booth Layout Manager
   */
  getBoothLayouts: async (fairId: number): Promise<BoothLayout[]> => {
    const response = await api.get(`/fairmanager/fairs/${fairId}/booth-layouts`);
    return response.data;
  },

  /**
   * Get booth amenities for a fair
   * Issue #1028 - Phase 2B: Booth Amenities Manager
   */
  getBoothAmenities: async (fairId: number): Promise<BoothAmenity[]> => {
    const response = await api.get(`/fairmanager/fairs/${fairId}/amenities`);
    return response.data;
  },

  /**
   * Get slots for a fair (simplified version for booth layout manager)
   * Issue #1028 - Phase 2B: Booth Layout Manager
   */
  getSlots: async (fairId: number): Promise<Slot[]> => {
    const response = await api.get(`/fairmanager/fairs/${fairId}/slots-simple`);
    return response.data;
  },

  // ========================================
  // Contact Settings Management (Issue #1076 - Phase 3 of #966)
  // ========================================

  /**
   * Get fair manager contact visibility settings for a specific fair
   * Issue #1076 - Phase 3: Contact Settings Management
   */
  getContactSettings: async (fairId: number): Promise<ContactSettingsResponse> => {
    try {
      const response = await api.get(`/fairmanager/contact-settings/${fairId}`);
      return response.data;
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      throw apiError;
    }
  },

  /**
   * Update fair manager contact visibility settings for a specific fair
   * Issue #1076 - Phase 3: Contact Settings Management
   */
  updateContactSettings: async (
    fairId: number,
    data: ContactSettingsUpdate
  ): Promise<ContactSettingsResponse> => {
    try {
      const response = await api.put(`/fairmanager/contact-settings/${fairId}`, data);
      return response.data;
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      throw apiError;
    }
  },

  // Get slot availability matrix for per-day bookings (Fair Manager) - Issue #1330
  getSlotAvailabilityMatrix: async (fairId: number): Promise<SlotAvailabilityMatrix> => {
    const response = await api.get(`/fairmanager/fairs/${fairId}/slots/availability`);
    return response.data;
  },
};

// Discount Code API - for Fair Managers and public validation (Issue #375)
export const discountCodeApi = {
  // Fair Manager methods - require authentication and can_edit permission
  async createDiscountCode(fairId: number, data: DiscountCodeCreate): Promise<DiscountCode> {
    const response = await api.post(`/fairmanager/fairs/${fairId}/discount-codes`, data);
    return response.data;
  },

  async getDiscountCodes(
    fairId: number,
    params?: {
      include_inactive?: boolean;
      page?: number;
      page_size?: number;
    }
  ): Promise<DiscountCodeListResponse> {
    const response = await api.get(`/fairmanager/fairs/${fairId}/discount-codes`, { params });
    return response.data;
  },

  async updateDiscountCode(codeId: number, data: DiscountCodeUpdate): Promise<DiscountCode> {
    const response = await api.put(`/fairmanager/discount-codes/${codeId}`, data);
    return response.data;
  },

  async deactivateDiscountCode(codeId: number): Promise<{ message: string }> {
    const response = await api.delete(`/fairmanager/discount-codes/${codeId}`);
    return response.data;
  },

  // Public method - no authentication required
  async validateDiscountCode(data: DiscountCodeValidateRequest): Promise<DiscountCodeValidateResponse> {
    const response = await api.post('/discount-codes/validate', data);
    return response.data;
  },

  // Get analytics for a discount code (Issue #385, #399, #460)
  async getDiscountCodeAnalytics(
    fairId: number,
    codeId: number,
    summaryOnly: boolean = false
  ): Promise<DiscountCodeAnalytics> {
    const response = await api.get(
      `/fairmanager/fairs/${fairId}/discount-codes/${codeId}/analytics`,
      { params: { summary_only: summaryOnly } }
    );
    return response.data;
  },
};

export const productCategoryApi = {
  async getCategories(): Promise<ProductCategoryResponse[]> {
    const response = await api.get('/product-categories');
    return response.data;
  },

  // Admin methods for managing product categories (Issue #173)
  async adminGetCategories(): Promise<ProductCategoryResponse[]> {
    const response = await api.get('/admin/product-categories');
    return response.data;
  },

  async adminCreateCategory(data: { name: string; display_name: string; description?: string; icon?: string; display_order?: number; is_active?: boolean }): Promise<ProductCategoryResponse> {
    const response = await api.post('/admin/product-categories', data);
    return response.data;
  },

  async adminUpdateCategory(id: number, data: { name?: string; display_name?: string; description?: string; icon?: string; display_order?: number; is_active?: boolean }): Promise<ProductCategoryResponse> {
    const response = await api.put(`/admin/product-categories/${id}`, data);
    return response.data;
  },

  async adminDeleteCategory(id: number): Promise<void> {
    await api.delete(`/admin/product-categories/${id}`);
  },
};

export const contactApi = {
  async submitForm(formData: {
    name: string;
    email: string;
    message: string;
    phone?: string;
  }): Promise<{status: string; message: string; data: unknown}> {
    const response = await api.post('/contact', formData);
    return response.data;
  },
};

// Generic API request function for use in components
export async function apiRequest(url: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', data?: unknown) {
  try {
    const response = await api.request({
      method,
      url,
      data,
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { detail?: string } }; message?: string };
    throw new Error(err?.response?.data?.detail || err?.message || 'Request failed');
  }
}

// ============================================================================
// User Profile API
// ============================================================================

export const userProfileApi = {
  // Get current user's profile (uses /auth/me endpoint)
  getProfile: async (): Promise<UserProfile> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // Update current user's profile
  // Note: Update functionality not yet implemented in API
  updateProfile: async (profileData: UserProfileUpdate): Promise<UserProfile> => {
    const response = await api.put('/auth/me', profileData);
    return response.data;
  },
};

// ============================================================================
// Favorites API
// ============================================================================

export const favoritesApi = {
  // Get user's favorite artist fairs
  getFavorites: async (): Promise<UserFavorite[]> => {
    const response = await api.get('/user/favorites');
    return response.data;
  },

  // Add fair to favorites
  addFavorite: async (fairId: number): Promise<UserFavorite> => {
    const response = await api.post('/user/favorites', { fair_id: fairId });
    return response.data;
  },

  // Remove fair from favorites
  removeFavorite: async (fairId: number): Promise<void> => {
    await api.delete(`/user/favorites/${fairId}`);
  },

  // Check if fair is favorited
  isFavorited: async (fairId: number, favorites: UserFavorite[]): Promise<boolean> => {
    return favorites.some(fav => fav.fair_id === fairId);
  },
};

// ============================================================================
// Reviews API
// ============================================================================

export const reviewsApi = {
  // Get user's reviews
  getUserReviews: async (
    page: number = 1,
    pageSize: number = 20
  ): Promise<{ items: Review[], total: number, page: number, page_size: number, total_pages: number }> => {
    const response = await api.get('/user/reviews', {
      params: { page, page_size: pageSize }
    });
    return response.data;
  },

  // Get all reviews for a artist fair (public) with pagination
  getFairReviews: async (
    fairId: number,
    page: number = 1,
    pageSize: number = 20
  ): Promise<{ items: Review[], total: number, page: number, page_size: number, total_pages: number }> => {
    const response = await api.get(`/user/fairs/${fairId}/reviews`, {
      params: { page, page_size: pageSize }
    });
    return response.data;
  },

  // Create a new review
  createReview: async (reviewData: ReviewCreate): Promise<Review> => {
    const response = await api.post('/user/reviews', reviewData);
    return response.data;
  },

  // Update an existing review
  updateReview: async (fairId: number, reviewData: ReviewUpdate): Promise<Review> => {
    const response = await api.put(`/user/reviews/${fairId}`, reviewData);
    return response.data;
  },

  // Delete a review
  deleteReview: async (fairId: number): Promise<void> => {
    await api.delete(`/user/reviews/${fairId}`);
  },
};

// ============================================================================
// Slot Search & Filter API (Issue #12)
// ============================================================================

export const slotApi = {
  // ============================================================================
  // Public Slot Endpoints
  // ============================================================================

  // Search slots with filters (public endpoint)
  searchSlots: async (fairId: number, filters: SlotFilters & { page?: number; page_size?: number }): Promise<SlotListResponse> => {
    const params: Record<string, string | number | boolean | undefined> = {
      page: filters.page || 1,
      page_size: filters.page_size || 12,
    };

    // Add optional filters
    if (filters.slot_type && filters.slot_type.length > 0) {
      params.slot_type = filters.slot_type.join(',');
    }
    if (filters.has_power !== undefined) {
      params.has_power = filters.has_power;
    }
    if (filters.min_price !== undefined) {
      params.min_price = filters.min_price;
    }
    if (filters.max_price !== undefined) {
      params.max_price = filters.max_price;
    }
    if (filters.is_available !== undefined) {
      params.is_available = filters.is_available;
    }
    if (filters.search) {
      params.search = filters.search;
    }

    const response = await api.get(`/fairs/${fairId}/slots`, { params });
    return response.data;
  },

  // Get single slot details (public endpoint - note: backend may not have this, check)
  getSlotDetails: async (slotId: number): Promise<Slot> => {
    const response = await api.get(`/slots/${slotId}`);
    return response.data;
  },

  // Check slot availability (optionally for specific dates)
  checkSlotAvailability: async (
    slotId: number,
    reservationType: 'full_duration' | 'per_day' = 'full_duration',
    selectedDates?: string[]
  ): Promise<SlotAvailabilityResponse> => {
    const params: Record<string, string | undefined> = {
      reservation_type: reservationType,
    };
    if (selectedDates && selectedDates.length > 0) {
      params.selected_dates = selectedDates.join(',');
    }
    const response = await api.get(`/slots/${slotId}/availability`, { params });
    return response.data;
  },

  // Get all slots for a fair (no pagination, for maps/layouts)
  getAllSlotsForFair: async (fairId: number): Promise<Slot[]> => {
    const response = await api.get(`/fairs/${fairId}/slots/all`);
    return response.data;
  },

  // Get slot availability summary for a fair (Issue #348)
  getSlotAvailabilitySummary: async (fairId: number): Promise<SlotAvailabilitySummary> => {
    const response = await api.get(`/fairs/${fairId}/slot-availability-summary`);
    return response.data;
  },

  // Compare 2-3 slots side-by-side (Issue #678, Issue #980)
  compareSlots: async (slotIds: number[]): Promise<SlotComparisonResponse> => {
    const response = await api.post('/slots/compare', { slot_ids: slotIds });
    return response.data;
  },

  // Get booth neighbors for a slot (Issue #676, Issue #980)
  getBoothNeighbors: async (slotId: number, maxDistance?: number): Promise<BoothNeighbor[]> => {
    const params: Record<string, number | undefined> = {};
    if (maxDistance !== undefined) {
      params.max_distance = maxDistance;
    }
    const response = await api.get(`/slots/${slotId}/neighbors`, { params });
    return response.data;
  },

  /**
   * Get distances from a booth to all amenities
   * Issue #952 - Complete booth map CRUD operations
   */
  getBoothDistances: async (slotId: number): Promise<BoothDistance[]> => {
    const response = await api.get(`/slots/${slotId}/distances`);
    return response.data;
  },

  // Match booth preferences for a fair (Issue #677, Issue #980)
  matchBoothPreferences: async (fairId: number, preferences: BoothMatchRequest): Promise<BoothMatchResult[]> => {
    const response = await api.post(`/fairs/${fairId}/slots/match-preferences`, preferences);
    return response.data;
  },

  // ============================================================================
  // Fair Manager Slot Management Endpoints (require authentication)
  // ============================================================================

  // Get all slots for a fair with reservation details (Fair Manager)
  getSlotsWithReservations: async (
    fairId: number,
    filters?: {
      status?: string;
      slot_type?: string;
      page?: number;
      page_size?: number;
    }
  ): Promise<SlotWithReservationListResponse> => {
    const params: Record<string, string | number | undefined> = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.slot_type) params.slot_type = filters.slot_type;
    if (filters?.page) params.page = filters.page;
    if (filters?.page_size) params.page_size = filters.page_size;

    const response = await api.get(`/fairmanager/fairs/${fairId}/slots`, { params });
    return response.data;
  },

  // Create a new slot for a fair (Fair Manager - requires can_manage_slots permission)
  createSlot: async (fairId: number, slotData: SlotCreate): Promise<SlotResponse> => {
    const response = await api.post(`/fairmanager/fairs/${fairId}/slots`, slotData);
    return response.data;
  },

  // Create multiple slots in bulk (Fair Manager)
  createSlotsBulk: async (fairId: number, bulkData: BulkSlotCreate): Promise<BulkSlotCreateResponse> => {
    const response = await api.post(`/fairmanager/fairs/${fairId}/slots/bulk`, bulkData);
    return response.data;
  },

  // Update a slot (Fair Manager - requires can_manage_slots permission)
  updateSlot: async (slotId: number, slotData: SlotUpdate): Promise<SlotResponse> => {
    const response = await api.put(`/fairmanager/slots/${slotId}`, slotData);
    return response.data;
  },

  // Delete a slot (Fair Manager - requires can_manage_slots permission)
  deleteSlot: async (slotId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/fairmanager/slots/${slotId}`);
    return response.data;
  },

  // Update slot price override (Fair Manager - Issue #152)
  updateSlotPriceOverride: async (slotId: number, priceData: SlotPriceOverrideRequest): Promise<SlotResponse> => {
    const response = await api.put(`/fairmanager/slots/${slotId}/price-override`, priceData);
    return response.data;
  },
};

// ============================================================================
// Slot Pricing Template API (Issue #152)
// ============================================================================

export const slotPricingApi = {
  // Get all pricing templates for a fair
  getTemplates: async (fairId: number, page = 1, pageSize = 50): Promise<SlotPricingTemplateListResponse> => {
    const response = await api.get(`/fairmanager/fairs/${fairId}/slot-pricing-templates`, {
      params: { page, page_size: pageSize }
    });
    return response.data;
  },

  // Create a new pricing template
  createTemplate: async (data: SlotPricingTemplateCreate): Promise<SlotPricingTemplate> => {
    const response = await api.post(`/fairmanager/fairs/${data.fair_id}/slot-pricing-templates`, data);
    return response.data;
  },

  // Update a pricing template
  updateTemplate: async (templateId: number, data: SlotPricingTemplateUpdate): Promise<SlotPricingTemplate> => {
    const response = await api.put(`/fairmanager/slot-pricing-templates/${templateId}`, data);
    return response.data;
  },

  // Delete a pricing template
  deleteTemplate: async (templateId: number): Promise<void> => {
    await api.delete(`/fairmanager/slot-pricing-templates/${templateId}`);
  },
};

// ============================================================================
// Vendor Profile API (Issue #13)
// ============================================================================

export const vendorApi = {
  // ========== Public Endpoints ==========

  // List active vendor profiles (public) (Issue #1132, #1198)
  getVendors: async (params?: { product_types?: string; min_rating?: number; page?: number; page_size?: number }): Promise<VendorListResponse> => {
    const response = await api.get('/vendors', { params });
    return response.data;
  },

  // Get vendor public profile by ID (public)
  getVendorPublicProfile: async (vendorId: number): Promise<VendorPublicProfile> => {
    const response = await api.get(`/vendors/${vendorId}`);
    return response.data;
  },

  // Get public product showcase for a vendor's customer profile (Issue #1200)
  getVendorPublicProducts: async (vendorId: number, limit = 6): Promise<VendorPublicProductItem[]> => {
    const response = await api.get(`/vendors/${vendorId}/products`, { params: { limit } });
    return response.data;
  },

  // Get upcoming fairs where vendor has confirmed reservations (Issue #1200)
  getVendorUpcomingFairs: async (vendorId: number): Promise<VendorUpcomingFair[]> => {
    const response = await api.get(`/vendors/${vendorId}/upcoming-fairs`);
    return response.data;
  },

  // ========== Authenticated User Endpoints ==========

  // List current user's vendor profiles
  getMyVendors: async (): Promise<Vendor[]> => {
    const response = await api.get('/user/vendors');
    return response.data;
  },

  // Create new vendor profile
  createVendor: async (data: VendorCreate): Promise<Vendor> => {
    const response = await api.post('/user/vendors', data);
    return response.data;
  },

  // Get own vendor profile details
  getMyVendor: async (vendorId: number): Promise<Vendor> => {
    const response = await api.get(`/user/vendors/${vendorId}`);
    return response.data;
  },

  // Update own vendor profile
  updateVendor: async (vendorId: number, data: VendorUpdate): Promise<Vendor> => {
    const response = await api.put(`/user/vendors/${vendorId}`, data);
    return response.data;
  },

  // Deactivate vendor profile (soft delete)
  deactivateVendor: async (vendorId: number): Promise<void> => {
    await api.delete(`/user/vendors/${vendorId}`);
  },

  // ========== Vendor Following Endpoints (Issue #1198) ==========

  // Follow a vendor (authenticated)
  followVendor: async (vendorId: number): Promise<void> => {
    await api.post('/user/followed-vendors', null, { params: { vendor_id: vendorId } });
  },

  // Unfollow a vendor (authenticated)
  unfollowVendor: async (vendorId: number): Promise<void> => {
    await api.delete(`/user/followed-vendors/${vendorId}`);
  },

  // Get list of followed vendor IDs (authenticated)
  getFollowedVendors: async (): Promise<{ vendor_ids: number[]; total: number; page: number; page_size: number; total_pages: number }> => {
    const response = await api.get('/user/followed-vendors', {
      params: { page: 1, page_size: 500 } // Get up to 500 followed vendors
    });
    return response.data;
  },

  // Upload portfolio image
  uploadPortfolioImage: async (vendorId: number, file: File): Promise<PortfolioImageUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/user/vendors/${vendorId}/portfolio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Remove portfolio image
  removePortfolioImage: async (vendorId: number, imageUrl: string): Promise<void> => {
    await api.delete(`/user/vendors/${vendorId}/portfolio`, {
      params: { image_url: imageUrl }
    });
  },

  // Get vendor's reservation history
  getVendorReservations: async (vendorId: number): Promise<VendorReservation[]> => {
    const response = await api.get(`/user/vendors/${vendorId}/reservations`);
    return response.data;
  },

  // ========== Vendor Product Management (Issue #83) ==========

  // List vendor's products (paginated)
  getVendorProducts: async (vendorId: number, page?: number, pageSize?: number): Promise<{ items: Product[]; total: number; page: number; page_size: number; total_pages: number }> => {
    const response = await api.get(`/user/vendors/${vendorId}/products`, {
      params: { page: page || 1, page_size: pageSize || 50 }
    });
    return response.data;
  },

  // Create product for vendor
  createVendorProduct: async (vendorId: number, data: Partial<Product>): Promise<Product> => {
    const response = await api.post(`/user/vendors/${vendorId}/products`, data);
    return response.data;
  },

  // Get single vendor product
  getVendorProduct: async (vendorId: number, productId: number): Promise<Product> => {
    const response = await api.get(`/user/vendors/${vendorId}/products/${productId}`);
    return response.data;
  },

  // Update vendor product
  updateVendorProduct: async (vendorId: number, productId: number, data: Partial<Product>): Promise<Product> => {
    const response = await api.put(`/user/vendors/${vendorId}/products/${productId}`, data);
    return response.data;
  },

  // Delete vendor product (soft delete)
  deleteVendorProduct: async (vendorId: number, productId: number): Promise<void> => {
    await api.delete(`/user/vendors/${vendorId}/products/${productId}`);
  },

  // ========== Fair Vendor Product Catalog (Issue #674) ==========

  // Add product to fair catalog
  addProductToFairCatalog: async (
    vendorId: number,
    fairId: number,
    data: FairVendorProductCreate
  ): Promise<FairVendorProduct> => {
    const response = await api.post(`/user/vendors/${vendorId}/fairs/${fairId}/products`, data);
    return response.data;
  },

  // Get vendor's products for a specific fair
  getVendorProductsForFair: async (
    vendorId: number,
    fairId: number,
    page?: number,
    pageSize?: number
  ): Promise<FairVendorProductListResponse> => {
    const response = await api.get(`/user/vendors/${vendorId}/fairs/${fairId}/products`, {
      params: { page: page || 1, page_size: pageSize || 50 }
    });
    return response.data;
  },

  // Update fair-specific product details
  updateFairProductDetails: async (
    vendorId: number,
    fairId: number,
    productId: number,
    data: FairVendorProductUpdate
  ): Promise<FairVendorProduct> => {
    const response = await api.put(`/user/vendors/${vendorId}/fairs/${fairId}/products/${productId}`, data);
    return response.data;
  },

  // Remove product from fair catalog
  removeProductFromFairCatalog: async (
    vendorId: number,
    fairId: number,
    productId: number
  ): Promise<void> => {
    await api.delete(`/user/vendors/${vendorId}/fairs/${fairId}/products/${productId}`);
  },

  // Browse products at a fair (public)
  browseFairProductsByVendor: async (
    fairId: number,
    vendorId?: number,
    page?: number,
    pageSize?: number
  ): Promise<FairVendorProductListResponse> => {
    const response = await api.get(`/public/fairs/${fairId}/products/by-vendor`, {
      params: {
        vendor_id: vendorId,
        page: page || 1,
        page_size: pageSize || 50
      }
    });
    return response.data;
  },

  // ========== Admin Endpoints ==========

  // Get all vendor profiles (admin only) with server-side search and filtering
  adminGetVendors: async (params?: {
    search?: string;
    product_types?: string;
    include_inactive?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<AdminVendorListResponse> => {
    const response = await api.get('/admin/vendors', { params });
    return response.data;
  },

  // Create vendor for a user (admin only)
  // If password is provided and user doesn't exist, a new user will be created
  adminCreateVendor: async (userEmail: string, data: VendorCreate, password?: string): Promise<Vendor> => {
    const params = new URLSearchParams({ user_email: userEmail });
    if (password) {
      params.append('user_password', password);
    }
    const response = await api.post(`/admin/vendors?${params.toString()}`, data);
    return response.data;
  },

  // Get vendor by ID (admin only)
  adminGetVendor: async (vendorId: number): Promise<Vendor> => {
    const response = await api.get(`/admin/vendors/${vendorId}`);
    return response.data;
  },

  // Update any vendor profile (admin only)
  adminUpdateVendor: async (vendorId: number, data: VendorUpdate): Promise<Vendor> => {
    const response = await api.put(`/admin/vendors/${vendorId}`, data);
    return response.data;
  },

  // Deactivate any vendor profile (admin only)
  adminDeactivateVendor: async (vendorId: number): Promise<void> => {
    await api.delete(`/admin/vendors/${vendorId}`);
  },

  // Get vendor's reservation history (admin only)
  adminGetVendorReservations: async (vendorId: number): Promise<VendorReservation[]> => {
    const response = await api.get(`/admin/vendors/${vendorId}/reservations`);
    return response.data;
  },

  // ========== Vendor Badge Endpoints (Issue #684, #955) ==========

  // Get all available badges (public)
  getAvailableBadges: async (): Promise<VendorBadgeListResponse> => {
    const response = await api.get('/vendor-badges');
    return response.data;
  },

  // Get vendor's earned badges (public)
  getVendorBadges: async (vendorId: number): Promise<VendorBadgeAward[]> => {
    const response = await api.get(`/vendors/${vendorId}/badges`);
    return response.data;
  },

  // Get badge progress for vendor (authenticated - vendor only)
  getVendorBadgeProgress: async (vendorId: number): Promise<VendorBadgeProgress[]> => {
    const response = await api.get(`/vendors/${vendorId}/badge-progress`);
    return response.data;
  },

  // Admin: Create new badge definition
  adminCreateBadge: async (data: VendorBadgeCreate): Promise<VendorBadge> => {
    const response = await api.post('/admin/vendor-badges', data);
    return response.data;
  },

  // Admin: Update badge definition
  adminUpdateBadge: async (badgeId: number, data: VendorBadgeUpdate): Promise<VendorBadge> => {
    const response = await api.put(`/admin/vendor-badges/${badgeId}`, data);
    return response.data;
  },

  // Admin: Delete badge definition
  adminDeleteBadge: async (badgeId: number): Promise<void> => {
    await api.delete(`/admin/vendor-badges/${badgeId}`);
  },

  // Admin: Manually award badge to vendor
  adminAwardBadge: async (vendorId: number, badgeId: number, data?: { notes?: string }): Promise<VendorBadgeAward> => {
    const response = await api.post(`/admin/vendors/${vendorId}/badges/${badgeId}`, data || {});
    return response.data;
  },

  // Admin: Revoke badge from vendor
  adminRevokeBadge: async (vendorId: number, awardId: number): Promise<void> => {
    await api.delete(`/admin/vendors/${vendorId}/badges/${awardId}`);
  },

  // Admin: Mark vendor as verified business
  adminVerifyVendor: async (vendorId: number, notes?: string): Promise<void> => {
    await api.post(`/admin/vendors/${vendorId}/verify`, { notes });
  },
};

// ========== Reservation API ==========
export const reservationApi = {
  // Check slot availability
  checkAvailability: async (
    slotId: number,
    reservationType: string,
    selectedDates?: string[]
  ): Promise<SlotAvailabilityCheck> => {
    const params: Record<string, string> = { reservation_type: reservationType };
    if (selectedDates && selectedDates.length > 0) {
      params.selected_dates = selectedDates.join(',');
    }
    const response = await api.get(`/slots/${slotId}/availability`, { params });
    return response.data;
  },

  // Create a new reservation
  createReservation: async (data: ReservationCreate): Promise<Reservation> => {
    const response = await api.post('/reservations', data);
    return response.data;
  },

  // Get current user's reservations (all vendors)
  getMyReservations: async (status?: string, page: number = 1, pageSize: number = 20): Promise<ReservationListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (status) {
      params.status = status;
    }
    const response = await api.get('/user/reservations', { params });
    return response.data;
  },

  // Get reservation details
  getReservationDetails: async (reservationId: number): Promise<ReservationDetail> => {
    const response = await api.get(`/user/reservations/${reservationId}`);
    return response.data;
  },

  // Update own reservation (pending only)
  updateReservation: async (reservationId: number, data: Partial<ReservationCreate>): Promise<Reservation> => {
    const response = await api.put(`/user/reservations/${reservationId}`, data);
    return response.data;
  },

  // Cancel own reservation
  cancelReservation: async (reservationId: number, reason?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (reason) {
      params.reason = reason;
    }
    await api.delete(`/user/reservations/${reservationId}`, { params });
  },

  // ========== Fair Manager Endpoints ==========

  // Get reservations for a managed fair
  getFairReservations: async (
    fairId: number,
    status?: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<ReservationListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (status) {
      params.status = status;
    }
    const response = await api.get(`/fair-manager/fairs/${fairId}/reservations`, { params });
    return response.data;
  },

  // Export fair reservations as CSV (Issue #378)
  exportFairReservationsCSV: async (fairId: number, status?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (status) {
      params.status = status;
    }
    const response = await api.get(`/fair-manager/fairs/${fairId}/reservations/export`, {
      params,
      responseType: 'blob', // Important for file download
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition'];
    let filename = `fair_${fairId}_reservations.csv`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Update reservation status (approve/reject)
  updateReservationStatus: async (
    reservationId: number,
    data: ReservationStatusUpdate
  ): Promise<Reservation> => {
    const response = await api.put(`/fair-manager/reservations/${reservationId}/status`, data);
    return response.data;
  },

  // ========== Admin Endpoints ==========

  // Get all reservations (admin)
  adminGetAllReservations: async (
    status?: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<ReservationListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (status) {
      params.status = status;
    }
    const response = await api.get('/admin/reservations', { params });
    return response.data;
  },

  // Update any reservation (admin)
  adminUpdateReservation: async (reservationId: number, data: Partial<ReservationCreate>): Promise<Reservation> => {
    const response = await api.put(`/admin/reservations/${reservationId}`, data);
    return response.data;
  },

  // Cancel any reservation (admin)
  adminCancelReservation: async (reservationId: number, reason?: string): Promise<void> => {
    const params: Record<string, string> = {};
    if (reason) {
      params.reason = reason;
    }
    await api.delete(`/admin/reservations/${reservationId}`, { params });
  },
};

// Waitlist API
export const waitlistApi = {
  // Join waitlist (vendor)
  joinWaitlist: async (data: WaitlistJoinRequest): Promise<WaitlistEntry> => {
    const response = await api.post('/user/waitlist', data);
    return response.data;
  },

  // Leave waitlist (vendor)
  leaveWaitlist: async (waitlistId: number): Promise<void> => {
    await api.delete(`/user/waitlist/${waitlistId}`);
  },

  // Check position in queue (vendor)
  checkPosition: async (vendorId: number, fairId: number): Promise<WaitlistPositionResponse> => {
    const response = await api.get('/user/waitlist/position', {
      params: { vendor_id: vendorId, fair_id: fairId }
    });
    return response.data;
  },

  // Get user's waitlist entries (vendor)
  getMyWaitlist: async (): Promise<WaitlistDetailResponse[]> => {
    const response = await api.get('/user/waitlist');
    return response.data;
  },

  // Accept slot offer (vendor)
  acceptOffer: async (waitlistId: number): Promise<Reservation> => {
    const response = await api.post(`/waitlist/${waitlistId}/accept`);
    return response.data;
  },

  // Decline slot offer (vendor)
  declineOffer: async (waitlistId: number, reason?: string): Promise<void> => {
    await api.post(`/waitlist/${waitlistId}/decline`, { reason });
  },

  // Get waitlist for fair (fair manager)
  getFairWaitlist: async (fairId: number, page: number = 1, pageSize: number = 50): Promise<WaitlistListResponse> => {
    const response = await api.get(`/fairmanager/fairs/${fairId}/waitlist`, {
      params: { page, page_size: pageSize }
    });
    return response.data;
  },

  // Get waitlist stats (fair manager)
  getFairWaitlistStats: async (fairId: number): Promise<WaitlistStatsResponse> => {
    const response = await api.get(`/fairmanager/fairs/${fairId}/waitlist-stats`);
    return response.data;
  },

  // Update waitlist position (fair manager)
  updatePosition: async (waitlistId: number, newPosition: number): Promise<WaitlistEntry> => {
    const response = await api.put(`/fairmanager/waitlist/${waitlistId}/position`, {
      new_position: newPosition
    });
    return response.data;
  },

  // Remove from waitlist (fair manager)
  removeFromWaitlist: async (waitlistId: number, reason?: string): Promise<void> => {
    await api.delete(`/fairmanager/waitlist/${waitlistId}`, {
      params: reason ? { reason } : undefined
    });
  },

  // Get system-wide analytics (admin)
  getAnalytics: async (): Promise<WaitlistAnalyticsResponse> => {
    const response = await api.get('/admin/waitlist-analytics');
    return response.data;
  },

  // Convert waitlist entry to reservation (admin)
  convertToReservation: async (waitlistId: number, slotId: number): Promise<Reservation> => {
    const response = await api.post(`/admin/waitlist/${waitlistId}/convert`, { slot_id: slotId });
    return response.data;
  },
};

// Notification API (Issue #496)
export const notificationApi = {
  // Get notifications for current user
  getNotifications: async (limit: number = 20, offset: number = 0): Promise<NotificationListResponse> => {
    const response = await api.get('/api/notifications', {
      params: { limit, offset }
    });
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (notificationId: number): Promise<Notification> => {
    const response = await api.post(`/api/notifications/${notificationId}/read`);
    return response.data;
  },

  // Mark all notifications as read
  markAllAsRead: async (): Promise<{ count: number }> => {
    const response = await api.post('/api/notifications/read-all');
    return response.data;
  },

  // Snooze notifications (Issue #823, #1109)
  snoozeNotifications: async (request: NotificationSnoozeRequest): Promise<NotificationSnoozeResponse> => {
    const response = await api.post('/api/notification-preferences/snooze', request);
    return response.data;
  },

  // Resume notifications (cancel snooze) (Issue #823, #1109)
  resumeNotifications: async (): Promise<NotificationSnoozeResponse> => {
    const response = await api.delete('/api/notification-preferences/snooze');
    return response.data;
  },

  // Get notification preferences (Issue #594, #823)
  getPreferences: async (): Promise<NotificationPreferences> => {
    const response = await api.get('/api/notification-preferences');
    return response.data;
  },

  // Update notification preferences (Issue #594, #823)
  updatePreferences: async (preferences: NotificationPreferencesUpdate): Promise<NotificationPreferences> => {
    const response = await api.put('/api/notification-preferences', preferences);
    return response.data;
  },
};

// Rental Equipment API (Issue #56)
export const rentalApi = {
  // Fair Manager endpoints
  getFairRentalItems: async (
    fairId: number,
    params?: { category?: string; include_inactive?: boolean; page?: number; page_size?: number }
  ): Promise<RentalItemListResponse> => {
    const response = await api.get(`/fairmanager/fairs/${fairId}/rental-items`, { params });
    return response.data;
  },

  createRentalItem: async (fairId: number, item: RentalItemCreate): Promise<RentalItem> => {
    const response = await api.post(`/fairmanager/fairs/${fairId}/rental-items`, item);
    return response.data;
  },

  updateRentalItem: async (itemId: number, updates: RentalItemUpdate): Promise<RentalItem> => {
    const response = await api.put(`/fairmanager/rental-items/${itemId}`, updates);
    return response.data;
  },

  deleteRentalItem: async (itemId: number): Promise<void> => {
    await api.delete(`/fairmanager/rental-items/${itemId}`);
  },

  getRentalStats: async (fairId: number): Promise<RentalStats> => {
    const response = await api.get(`/fairmanager/fairs/${fairId}/rental-stats`);
    return response.data;
  },

  // Fair Manager Dashboard (Issue #154)
  getDashboardStats: async (): Promise<FairManagerDashboardStats> => {
    const response = await api.get('/fairmanager/dashboard-stats');
    return response.data;
  },

  getPayouts: async (params?: { fair_id?: number; page?: number; page_size?: number }): Promise<FairManagerPayoutsResponse> => {
    const response = await api.get('/fairmanager/payouts', { params });
    return response.data;
  },

  // Public endpoint
  browseRentalItems: async (
    fairId: number,
    params?: { category?: string; page?: number; page_size?: number }
  ): Promise<RentalItemListResponse> => {
    const response = await api.get(`/fairs/${fairId}/rental-items`, { params });
    return response.data;
  },

  // Vendor endpoints
  addRentalToReservation: async (
    reservationId: number,
    rental: ReservationRentalItemCreate
  ): Promise<ReservationRentalItem> => {
    const response = await api.post(`/reservations/${reservationId}/rental-items`, rental);
    return response.data;
  },

  removeRentalFromReservation: async (
    reservationId: number,
    rentalItemId: number
  ): Promise<void> => {
    await api.delete(`/reservations/${reservationId}/rental-items/${rentalItemId}`);
  },

  getReservationRentals: async (reservationId: number): Promise<ReservationRentalItem[]> => {
    const response = await api.get(`/reservations/${reservationId}/rental-items`);
    return response.data;
  },
};

// Advertisement Package & Purchase API (Issue #86)
export const adPackageApi = {
  // Public endpoints
  getPackages: async (params?: {
    season?: 'spring' | 'summer' | 'fall' | 'winter' | 'year_round';
    is_active?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<AdPackageListResponse> => {
    const response = await api.get('/ad-packages', { params });
    return response.data;
  },

  // Vendor endpoints
  createPurchase: async (purchase: AdPurchaseCreate): Promise<AdPurchase> => {
    const response = await api.post('/vendor/ad-purchases', purchase);
    return response.data;
  },

  getMyPurchases: async (params?: {
    page?: number;
    page_size?: number;
  }): Promise<AdPurchaseListResponse> => {
    const response = await api.get('/vendor/ad-purchases', { params });
    return response.data;
  },

  getPurchaseById: async (purchaseId: number): Promise<AdPurchase> => {
    const response = await api.get(`/vendor/ad-purchases/${purchaseId}`);
    return response.data;
  },

  // Stripe payment integration (Issue #98)
  createCheckoutSession: async (purchaseId: number): Promise<{ checkout_url: string; purchase_id: number; amount_cents: number }> => {
    const response = await api.post(`/vendor/ad-purchases/${purchaseId}/create-checkout`);
    return response.data;
  },

  // Admin endpoints
  adminGetPackages: async (params?: {
    season?: 'spring' | 'summer' | 'fall' | 'winter' | 'year_round';
    is_active?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<AdPackageListResponse> => {
    const response = await api.get('/admin/ad-packages', { params });
    return response.data;
  },

  adminCreatePackage: async (packageData: AdPackageCreate): Promise<AdPackage> => {
    const response = await api.post('/admin/ad-packages', packageData);
    return response.data;
  },

  adminUpdatePackage: async (packageId: number, packageData: AdPackageUpdate): Promise<AdPackage> => {
    const response = await api.put(`/admin/ad-packages/${packageId}`, packageData);
    return response.data;
  },

  adminDeletePackage: async (packageId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/ad-packages/${packageId}`);
    return response.data;
  },

  adminGetPurchases: async (params?: {
    payment_status?: 'pending' | 'completed' | 'failed' | 'refunded';
    page?: number;
    page_size?: number;
  }): Promise<AdPurchaseListResponse> => {
    const response = await api.get('/admin/ad-purchases', { params });
    return response.data;
  },

  adminUpdatePurchaseStatus: async (
    purchaseId: number,
    statusUpdate: AdPurchaseStatusUpdate
  ): Promise<AdPurchase> => {
    const response = await api.put(`/admin/ad-purchases/${purchaseId}/status`, statusUpdate);
    return response.data;
  },

  adminGetAnalytics: async (): Promise<AdPackageAnalytics> => {
    const response = await api.get('/admin/ad-packages/analytics');
    return response.data;
  },
};

// Admin Vendor Product API (Issue #120)
export const adminVendorProductApi = {
  getVendorProducts: async (
    vendorId: number,
    page: number = 1,
    pageSize: number = 50,
    category?: string,
    includeDeleted: boolean = false
  ): Promise<ProductListResponse> => {
    const params: Record<string, string | number | boolean> = { page, page_size: pageSize };
    if (category) params.category = category;
    if (includeDeleted) params.include_deleted = includeDeleted;
    const response = await api.get(`/admin/vendors/${vendorId}/products`, { params });
    return response.data;
  },

  createVendorProduct: async (
    vendorId: number,
    product: ProductCreate
  ): Promise<Product> => {
    const response = await api.post(`/admin/vendors/${vendorId}/products`, product);
    return response.data;
  },

  getVendorProduct: async (
    vendorId: number,
    productId: number
  ): Promise<Product> => {
    const response = await api.get(`/admin/vendors/${vendorId}/products/${productId}`);
    return response.data;
  },

  updateVendorProduct: async (
    vendorId: number,
    productId: number,
    product: ProductUpdate
  ): Promise<Product> => {
    const response = await api.put(`/admin/vendors/${vendorId}/products/${productId}`, product);
    return response.data;
  },

  deleteVendorProduct: async (
    vendorId: number,
    productId: number
  ): Promise<void> => {
    await api.delete(`/admin/vendors/${vendorId}/products/${productId}`);
  },

  restoreVendorProduct: async (
    vendorId: number,
    productId: number
  ): Promise<Product> => {
    const response = await api.post(`/admin/vendors/${vendorId}/products/${productId}/restore`);
    return response.data;
  },
};

// Feature Flag API (Issue #167)
export const featureFlagApi = {
  // Public endpoint - check if feature is enabled (no auth required)
  checkFlag: async (flagKey: string): Promise<FeatureFlagStatusResponse> => {
    const response = await api.get(`/feature-flags/${flagKey}`);
    return response.data;
  },

  // Admin endpoints
  getAllFlags: async (): Promise<FeatureFlagListResponse> => {
    const response = await api.get('/admin/feature-flags');
    return response.data;
  },

  createFlag: async (flagData: FeatureFlagCreate): Promise<FeatureFlag> => {
    const response = await api.post('/admin/feature-flags', flagData);
    return response.data;
  },

  updateFlag: async (
    flagKey: string,
    updateData: FeatureFlagUpdate
  ): Promise<FeatureFlag> => {
    const response = await api.put(`/admin/feature-flags/${flagKey}`, updateData);
    return response.data;
  },

  deleteFlag: async (flagKey: string): Promise<void> => {
    await api.delete(`/admin/feature-flags/${flagKey}`);
  },
};

// Fair Manager Request API (Issue #148 Frontend, Issue #177)
export const fairManagerRequestApi = {
  getMyRequest: async (): Promise<FairManagerRequestResponse> => {
    const response = await api.get('/user/fair-manager-request');
    return response.data;
  },

  submitRequest: async (data: FairManagerRequestCreate): Promise<FairManagerRequestResponse> => {
    const response = await api.post('/user/fair-manager-request', data);
    return response.data;
  },
};

// Admin Fair Manager Request API (Issue #148 Frontend, Issue #177)
export const adminFairManagerRequestApi = {
  getRequests: async (
    status?: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<FairManagerRequestListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (status) params.status = status;
    const response = await api.get('/admin/fair-manager-requests', { params });
    return response.data;
  },

  approveRequest: async (requestId: number): Promise<FairManagerRequestResponse> => {
    const response = await api.post(`/admin/fair-manager-requests/${requestId}/approve`);
    return response.data;
  },

  rejectRequest: async (
    requestId: number,
    adminNotes?: string
  ): Promise<FairManagerRequestResponse> => {
    const response = await api.post(`/admin/fair-manager-requests/${requestId}/reject`, {
      admin_notes: adminNotes,
    });
    return response.data;
  },
};

// Admin Reservation Management API (Issue #972)
export const adminReservationApi = {
  getReservations: async (
    statusFilter?: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<AdminReservationListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (statusFilter) params.status_filter = statusFilter;
    const response = await api.get('/admin/reservations', { params });
    return response.data;
  },

  updateReservation: async (
    reservationId: number,
    data: AdminReservationUpdate
  ): Promise<AdminReservationResponse> => {
    const response = await api.put(`/admin/reservations/${reservationId}`, data);
    return response.data;
  },

  cancelReservation: async (
    reservationId: number,
    reason?: string
  ): Promise<void> => {
    const params: Record<string, string> = {};
    if (reason) params.reason = reason;
    await api.delete(`/admin/reservations/${reservationId}`, { params });
  },
};

// Admin Review Moderation API (Issue #973)
export const adminReviewModerationApi = {
  /**
   * Get all vendor reviews (admin only)
   * Includes unpublished and flagged reviews
   */
  getAllReviews: async (
    page: number = 1,
    pageSize: number = 20,
    rating?: number,
    vendorId?: number,
    isFlagged?: boolean,
    isPublished?: boolean,
    sortBy: string = 'recent'
  ): Promise<VendorReviewListResponse> => {
    const params: Record<string, string | number | boolean> = { page, page_size: pageSize, sort_by: sortBy };
    if (rating !== undefined) params.rating = rating;
    if (vendorId !== undefined) params.vendor_id = vendorId;
    if (isFlagged !== undefined) params.is_flagged = isFlagged;
    if (isPublished !== undefined) params.is_published = isPublished;
    const response = await api.get('/admin/reviews', { params });
    return response.data;
  },

  /**
   * Moderate a vendor review (flag, unpublish, add notes)
   */
  moderateReview: async (reviewId: number, data: VendorReviewAdminUpdate): Promise<VendorReview> => {
    const response = await api.put(`/admin/reviews/${reviewId}/moderate`, data);
    return response.data;
  },
};

// Advertisement API (Issue #350)
export const advertisementApi = {
  /**
   * Get active advertisements for public display
   */
  getActiveAds: async (adType?: string, limit: number = 10): Promise<AdvertisementListResponse> => {
    const params: Record<string, string | number> = { limit };
    if (adType) params.ad_type = adType;
    const response = await api.get('/advertisements', { params });
    return response.data;
  },

  /**
   * Track advertisement impression
   */
  trackImpression: async (adId: number): Promise<void> => {
    await api.post(`/advertisements/${adId}/impression`);
  },

  /**
   * Track advertisement click
   */
  trackClick: async (adId: number): Promise<void> => {
    await api.post(`/advertisements/${adId}/click`);
  },
};

// Admin Advertisement Management API (Issue #985)
export const adminAdvertisementApi = {
  /**
   * Get all advertisements for admin management (with pagination and filtering)
   */
  getAdvertisements: async (params?: {
    page?: number;
    page_size?: number;
    include_inactive?: boolean;
    ad_type?: AdType;
  }): Promise<AdvertisementListResponse> => {
    const response = await api.get('/admin/advertisements', { params });
    return response.data;
  },

  /**
   * Get a single advertisement by ID
   */
  getAdvertisement: async (adId: number): Promise<Advertisement> => {
    const response = await api.get(`/admin/advertisements/${adId}`);
    return response.data;
  },

  /**
   * Create a new advertisement
   */
  createAdvertisement: async (data: AdvertisementCreate): Promise<Advertisement> => {
    const response = await api.post('/admin/advertisements', data);
    return response.data;
  },

  /**
   * Update an existing advertisement
   */
  updateAdvertisement: async (adId: number, data: AdvertisementUpdate): Promise<Advertisement> => {
    const response = await api.put(`/admin/advertisements/${adId}`, data);
    return response.data;
  },

  /**
   * Delete an advertisement (soft delete - sets is_active to false)
   */
  deleteAdvertisement: async (adId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/advertisements/${adId}`);
    return response.data;
  },

  /**
   * Toggle advertisement active status
   */
  toggleAdvertisementStatus: async (adId: number, isActive: boolean): Promise<Advertisement> => {
    const response = await api.put(`/admin/advertisements/${adId}`, { is_active: isActive });
    return response.data;
  },
};

// Vendor Advertisement Management API (Issue #1175)
export const vendorAdvertisementApi = {
  /**
   * Get all advertisements owned by the current vendor (with pagination)
   */
  getAdvertisements: async (params?: {
    includeInactive?: boolean;
    page?: number;
    pageSize?: number;
  }): Promise<AdvertisementListResponse> => {
    const response = await api.get('/vendor/advertisements', { params });
    return response.data;
  },

  /**
   * Get a specific advertisement owned by the current vendor
   */
  getAdvertisement: async (adId: number): Promise<Advertisement> => {
    const response = await api.get(`/vendor/advertisements/${adId}`);
    return response.data;
  },

  /**
   * Update an advertisement owned by the current vendor
   * Note: Vendors can only update content fields (title, description, URLs, display_text)
   * Admin-only fields (ad_type, priority, dates) cannot be modified
   */
  updateAdvertisement: async (adId: number, data: VendorAdvertisementUpdate): Promise<Advertisement> => {
    const response = await api.put(`/vendor/advertisements/${adId}`, data);
    return response.data;
  },

  /**
   * Delete (deactivate) an advertisement owned by the current vendor
   * Note: This is a soft delete that sets is_active to false
   */
  deleteAdvertisement: async (adId: number): Promise<void> => {
    await api.delete(`/vendor/advertisements/${adId}`);
  },

  /**
   * Get analytics for a specific advertisement owned by the current vendor
   */
  getAdvertisementAnalytics: async (adId: number): Promise<AdvertisementAnalytics[]> => {
    const response = await api.get(`/vendor/advertisements/${adId}/analytics`);
    return response.data;
  },
};

// ============================================================================
// Fair Inquiry System API (Issue #377)
// ============================================================================

// Vendor Inquiry API
export const vendorInquiryApi = {
  /**
   * Create a new inquiry for a fair
   */
  createInquiry: async (data: FairInquiryCreate): Promise<FairInquiryResponse> => {
    const response = await api.post('/vendor/inquiries', data);
    return response.data;
  },

  /**
   * Get all inquiries for the current vendor
   */
  getMyInquiries: async (
    fairId?: number,
    status?: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<FairInquiryListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (fairId) params.fair_id = fairId;
    if (status) params.status = status;
    const response = await api.get('/vendor/inquiries', { params });
    return response.data;
  },

  /**
   * Get detailed inquiry with messages
   */
  getInquiryDetail: async (inquiryId: number): Promise<FairInquiryDetailResponse> => {
    const response = await api.get(`/vendor/inquiries/${inquiryId}`);
    return response.data;
  },

  /**
   * Add a message to an inquiry
   */
  addMessage: async (data: InquiryMessageCreate): Promise<InquiryMessageResponse> => {
    const response = await api.post('/vendor/inquiries/messages', data);
    return response.data;
  },

  /**
   * Mark messages as read
   */
  markMessagesAsRead: async (inquiryId: number): Promise<void> => {
    await api.put(`/vendor/inquiries/${inquiryId}/mark-read`);
  },
};

// Fair Manager Inquiry API
export const fairManagerInquiryApi = {
  /**
   * Get all inquiries for managed fairs
   */
  getFairInquiries: async (
    fairId: number,
    status?: string,
    priority?: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<FairInquiryListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (status) params.status = status;
    if (priority) params.priority = priority;
    const response = await api.get(`/fairmanager/fairs/${fairId}/inquiries`, { params });
    return response.data;
  },

  /**
   * Get detailed inquiry with messages
   */
  getInquiryDetail: async (inquiryId: number): Promise<FairInquiryDetailResponse> => {
    const response = await api.get(`/fairmanager/inquiries/${inquiryId}`);
    return response.data;
  },

  /**
   * Add a response message to an inquiry
   */
  addResponse: async (data: InquiryMessageCreate): Promise<InquiryMessageResponse> => {
    const response = await api.post('/fairmanager/inquiries/messages', data);
    return response.data;
  },

  /**
   * Update inquiry status and/or priority
   */
  updateStatus: async (
    inquiryId: number,
    data: InquiryStatusUpdate
  ): Promise<FairInquiryResponse> => {
    const response = await api.put(`/fairmanager/inquiries/${inquiryId}/status`, data);
    return response.data;
  },

  /**
   * Mark messages as read
   */
  markMessagesAsRead: async (inquiryId: number): Promise<void> => {
    await api.put(`/fairmanager/inquiries/${inquiryId}/mark-read`);
  },
};

// Vendor Booth Preferences API (Issue #677)
export const boothPreferencesApi = {
  /**
   * Get vendor's saved booth preferences
   */
  getPreferences: async (vendorId: number): Promise<VendorBoothPreferences> => {
    const response = await api.get(`/vendors/${vendorId}/booth-preferences`);
    return response.data;
  },

  /**
   * Update vendor's booth preferences
   */
  updatePreferences: async (
    vendorId: number,
    preferences: VendorBoothPreferencesUpdate
  ): Promise<VendorBoothPreferences> => {
    const response = await api.put(`/vendors/${vendorId}/booth-preferences`, preferences);
    return response.data;
  },

  /**
   * Find booths matching vendor preferences with smart scoring
   * Returns ranked list of available slots with match scores
   */
  matchPreferences: async (
    fairId: number,
    request: BoothMatchRequest
  ): Promise<BoothMatchResult[]> => {
    const response = await api.post(`/fairs/${fairId}/slots/match-preferences`, request);
    return response.data;
  },
};

// ========================================
// Issue #679: Expense & Revenue Tracking for ROI Calculation
// ========================================

export const expenseApi = {
  /**
   * Create a new vendor fair expense
   */
  createExpense: async (
    vendorId: number,
    fairId: number,
    expense: VendorFairExpenseCreate
  ): Promise<VendorFairExpense> => {
    const response = await api.post(`/vendors/${vendorId}/fairs/${fairId}/expenses`, expense);
    return response.data;
  },

  /**
   * Get expenses for a vendor at a specific fair
   * Issue #768: Added pagination support
   */
  getExpenses: async (
    vendorId: number,
    fairId: number,
    page?: number,
    pageSize?: number
  ): Promise<VendorFairExpense[] | PaginatedExpenseResponse> => {
    const params = new URLSearchParams();
    if (page !== undefined && pageSize !== undefined) {
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
    }
    const queryString = params.toString();
    const url = `/vendors/${vendorId}/fairs/${fairId}/expenses${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Update a vendor fair expense
   */
  updateExpense: async (
    vendorId: number,
    expenseId: number,
    expense: VendorFairExpenseUpdate
  ): Promise<VendorFairExpense> => {
    const response = await api.put(`/vendors/${vendorId}/expenses/${expenseId}`, expense);
    return response.data;
  },

  /**
   * Delete a vendor fair expense
   */
  deleteExpense: async (vendorId: number, expenseId: number): Promise<void> => {
    await api.delete(`/vendors/${vendorId}/expenses/${expenseId}`);
  },
};

export const revenueApi = {
  /**
   * Create a new vendor fair revenue
   */
  createRevenue: async (
    vendorId: number,
    fairId: number,
    revenue: VendorFairRevenueCreate
  ): Promise<VendorFairRevenue> => {
    const response = await api.post(`/vendors/${vendorId}/fairs/${fairId}/revenue`, revenue);
    return response.data;
  },

  /**
   * Get revenue for a vendor at a specific fair
   * Issue #768: Added pagination support
   */
  getRevenue: async (
    vendorId: number,
    fairId: number,
    page?: number,
    pageSize?: number
  ): Promise<VendorFairRevenue[] | PaginatedRevenueResponse> => {
    const params = new URLSearchParams();
    if (page !== undefined && pageSize !== undefined) {
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());
    }
    const queryString = params.toString();
    const url = `/vendors/${vendorId}/fairs/${fairId}/revenue${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Update a vendor fair revenue
   */
  updateRevenue: async (
    vendorId: number,
    revenueId: number,
    revenue: VendorFairRevenueUpdate
  ): Promise<VendorFairRevenue> => {
    const response = await api.put(`/vendors/${vendorId}/revenue/${revenueId}`, revenue);
    return response.data;
  },

  /**
   * Delete a vendor fair revenue
   */
  deleteRevenue: async (vendorId: number, revenueId: number): Promise<void> => {
    await api.delete(`/vendors/${vendorId}/revenue/${revenueId}`);
  },
};

export const roiApi = {
  /**
   * Calculate ROI for a vendor at a specific fair
   */
  getFairROI: async (vendorId: number, fairId: number): Promise<FairROICalculation> => {
    const response = await api.get(`/vendors/${vendorId}/fairs/${fairId}/roi`);
    return response.data;
  },

  /**
   * Calculate lifetime ROI for a vendor across all fairs
   */
  getLifetimeROI: async (vendorId: number): Promise<VendorLifetimeROI> => {
    const response = await api.get(`/vendors/${vendorId}/lifetime-roi`);
    return response.data;
  },
};

// ============================================================================
// Vendor Fair Metrics API (Issue #682)
// Post-Fair Survey & Satisfaction Tracking
// ============================================================================

export const vendorFairMetricsApi = {
  /**
   * Submit vendor post-fair survey metrics
   */
  submitMetrics: async (
    vendorId: number,
    fairId: number,
    metrics: VendorFairMetricsCreate
  ): Promise<VendorFairMetricsResponse> => {
    const response = await api.post(`/vendors/${vendorId}/fairs/${fairId}/metrics`, metrics);
    return response.data;
  },

  /**
   * Get vendor metrics for a specific fair
   */
  getMetrics: async (vendorId: number, fairId: number): Promise<VendorFairMetricsResponse> => {
    const response = await api.get(`/vendors/${vendorId}/fairs/${fairId}/metrics`);
    return response.data;
  },

  /**
   * Update vendor post-fair metrics
   */
  updateMetrics: async (
    vendorId: number,
    fairId: number,
    metrics: VendorFairMetricsUpdate
  ): Promise<VendorFairMetricsResponse> => {
    const response = await api.put(`/vendors/${vendorId}/fairs/${fairId}/metrics`, metrics);
    return response.data;
  },

  /**
   * Get vendor satisfaction trends over time
   */
  getSatisfactionTrends: async (vendorId: number): Promise<VendorSatisfactionTrend[]> => {
    const response = await api.get(`/vendors/${vendorId}/analytics/satisfaction-trends`);
    return response.data;
  },
};

// ============================================================================
// Vendor Analytics API (Issue #982, #681, #680)
// Profile views, search performance, conversion, business intelligence
// ============================================================================

export interface DateRangeParams {
  start_date?: string; // ISO datetime
  end_date?: string; // ISO datetime
}

export interface ProfileViewsParams extends DateRangeParams {
  granularity?: 'day' | 'week' | 'month';
}

export const vendorAnalyticsApi = {
  /**
   * Get vendor profile views analytics (Issue #681)
   * Tracks profile page visits and view sources
   */
  getProfileViews: async (
    vendorId: number,
    params?: ProfileViewsParams
  ): Promise<ProfileViewsAnalyticsResponse> => {
    const response = await api.get(`/vendors/${vendorId}/analytics/profile-views`, { params });
    return response.data;
  },

  /**
   * Get vendor search performance analytics (Issue #681)
   * Tracks search impressions, clicks, and CTR
   */
  getSearchPerformance: async (
    vendorId: number,
    params?: DateRangeParams
  ): Promise<SearchPerformanceResponse> => {
    const response = await api.get(`/vendors/${vendorId}/analytics/search-performance`, { params });
    return response.data;
  },

  /**
   * Get vendor conversion funnel analytics (Issue #681)
   * Tracks profile views -> search clicks -> reservations
   */
  getConversionFunnel: async (
    vendorId: number,
    params?: DateRangeParams
  ): Promise<ConversionFunnelResponse> => {
    const response = await api.get(`/vendors/${vendorId}/analytics/conversion-funnel`, { params });
    return response.data;
  },

  /**
   * Get vendor discovery sources analytics (Issue #681)
   * Tracks how customers discover vendors before booking
   */
  getDiscoverySources: async (
    vendorId: number,
    params?: DateRangeParams
  ): Promise<DiscoverySourcesResponse> => {
    const response = await api.get(`/vendors/${vendorId}/analytics/discovery-sources`, { params });
    return response.data;
  },

  /**
   * Get vendor product rankings (Issue #680)
   * Top/bottom performing products by revenue or units sold
   */
  getProductRankings: async (
    vendorId: number,
    limit: number = 10,
    metric: 'revenue' | 'units_sold' = 'revenue'
  ): Promise<ProductRanking[]> => {
    const response = await api.get(`/vendors/${vendorId}/analytics/product-rankings`, {
      params: { limit, metric }
    });
    return response.data;
  },

  /**
   * Get fair comparison data (Issue #680)
   * Compare multiple fairs side-by-side (ROI, revenue, expenses)
   */
  getFairComparison: async (
    vendorId: number,
    fairIds: number[]
  ): Promise<FairComparisonData[]> => {
    if (!fairIds || fairIds.length === 0) {
      throw new Error('fairIds array cannot be empty');
    }
    const response = await api.get(`/vendors/${vendorId}/analytics/fair-comparison`, {
      params: { fair_ids: fairIds.join(',') }
    });
    return response.data;
  },

  /**
   * Get seasonal insights (Issue #680)
   * Monthly aggregated sales data showing best times for sales
   */
  getSeasonalInsights: async (vendorId: number): Promise<SeasonalInsight[]> => {
    const response = await api.get(`/vendors/${vendorId}/analytics/seasonal-insights`);
    return response.data;
  },

  /**
   * Get vendor satisfaction trends over time (Issue #682)
   * Alias for vendorFairMetricsApi.getSatisfactionTrends for consistency
   */
  getSatisfactionTrends: async (vendorId: number): Promise<VendorSatisfactionTrend[]> => {
    const response = await api.get(`/vendors/${vendorId}/analytics/satisfaction-trends`);
    return response.data;
  },
};

// ============================================================================
// Vendor Review API (Issue #683, #957)
// ============================================================================

export const vendorReviewApi = {
  /**
   * Get reviews for a specific vendor (public)
   * Issue #683, #956
   */
  getVendorReviews: async (
    vendorId: number,
    page: number = 1,
    pageSize: number = 20,
    sortBy: 'recent' | 'helpful' | 'rating_high' | 'rating_low' = 'recent',
    ratingFilter?: number
  ): Promise<VendorReviewListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize, sort_by: sortBy };
    if (ratingFilter !== undefined) {
      params.rating = ratingFilter;
    }
    const response = await api.get(`/vendors/${vendorId}/reviews`, { params });
    return response.data;
  },

  /**
   * Get vendor rating summary (public)
   * Returns average rating, total reviews, rating distribution, and verified review count
   * Issue #683, #956
   */
  getRatingSummary: async (vendorId: number): Promise<VendorRatingSummary> => {
    const response = await api.get(`/vendors/${vendorId}/rating-summary`);
    return response.data;
  },

  /**
   * Check if current user is eligible to review a vendor for a specific fair
   * Issue #683, #956
   */
  checkReviewEligibility: async (
    vendorId: number,
    fairId: number
  ): Promise<VendorReviewEligibility> => {
    const response = await api.get(`/vendors/${vendorId}/fairs/${fairId}/review-eligibility`);
    return response.data;
  },

  /**
   * Create a new vendor review (authenticated user)
   * Issue #683, #956
   */
  createReview: async (
    vendorId: number,
    reviewData: VendorReviewCreate
  ): Promise<VendorReview> => {
    const response = await api.post(`/vendors/${vendorId}/reviews`, reviewData);
    return response.data;
  },

  /**
   * Update own review (authenticated user)
   * Issue #683, #956
   */
  updateReview: async (
    reviewId: number,
    reviewData: VendorReviewUpdate
  ): Promise<VendorReview> => {
    const response = await api.put(`/user/reviews/${reviewId}`, reviewData);
    return response.data;
  },

  /**
   * Delete own review (authenticated user)
   * Issue #683, #956
   */
  deleteReview: async (reviewId: number): Promise<void> => {
    await api.delete(`/user/reviews/${reviewId}`);
  },

  /**
   * Mark review as helpful (authenticated user)
   * Issue #683, #956
   */
  markReviewHelpful: async (reviewId: number): Promise<void> => {
    await api.post(`/reviews/${reviewId}/helpful`);
  },

  /**
   * Unmark review as helpful (authenticated user)
   * Issue #683, #956
   */
  unmarkReviewHelpful: async (reviewId: number): Promise<void> => {
    await api.delete(`/reviews/${reviewId}/helpful`);
  },
};

// Product Pre-Order API (Issue #951, #675)
export const preOrderApi = {
  /**
   * Create a pre-order for a product at a fair (Customer)
   */
  createPreOrder: async (
    fairId: number,
    vendorId: number,
    preOrderData: ProductPreOrderCreate
  ): Promise<ProductPreOrder> => {
    const response = await api.post(
      `/fairs/${fairId}/vendors/${vendorId}/preorders`,
      preOrderData
    );
    return response.data;
  },

  /**
   * Get all pre-orders for the current customer
   */
  getCustomerPreOrders: async (
    page: number = 1,
    pageSize: number = 50,
    statusFilter?: string
  ): Promise<ProductPreOrderListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (statusFilter) params.status_filter = statusFilter;
    const response = await api.get('/user/preorders', { params });
    return response.data;
  },

  /**
   * Cancel a pre-order (Customer)
   */
  cancelPreOrder: async (orderId: number): Promise<ProductPreOrder> => {
    const response = await api.put(`/user/preorders/${orderId}/cancel`);
    return response.data;
  },

  /**
   * Get all pre-orders for a vendor
   */
  getVendorPreOrders: async (
    vendorId: number,
    page: number = 1,
    pageSize: number = 50,
    fairId?: number,
    statusFilter?: string
  ): Promise<ProductPreOrderListResponse> => {
    const params: Record<string, string | number> = { page, page_size: pageSize };
    if (fairId) params.fair_id = fairId;
    if (statusFilter) params.status_filter = statusFilter;
    const response = await api.get(`/vendors/${vendorId}/preorders`, { params });
    return response.data;
  },

  /**
   * Update pre-order status (Vendor)
   */
  updatePreOrderStatus: async (
    vendorId: number,
    orderId: number,
    updateData: ProductPreOrderUpdate
  ): Promise<ProductPreOrder> => {
    const response = await api.put(
      `/vendors/${vendorId}/preorders/${orderId}/status`,
      updateData
    );
    return response.data;
  },

  /**
   * Mark pre-order as picked up using confirmation code (Vendor)
   */
  markPickedUp: async (
    vendorId: number,
    orderId: number,
    request: MarkPickedUpRequest
  ): Promise<ProductPreOrder> => {
    const response = await api.post(
      `/vendors/${vendorId}/preorders/${orderId}/mark-picked-up`,
      request
    );
    return response.data;
  },
};

// ============================================================================
// Role Management API (Issue #969)
// Admin role and permission management with type safety
// ============================================================================

export const roleManagementApi = {
  /**
   * Get all roles with optional user counts
   */
  getAllRoles: async (params?: { include_user_counts?: boolean; limit?: number }): Promise<Role[]> => {
    const response = await api.get('/api/roles/', { params });
    return response.data;
  },

  /**
   * Assign a role to a user
   */
  assignRole: async (data: UserRoleAssignRequest): Promise<UserRoleResponse> => {
    const response = await api.post('/api/roles/assign', data);
    return response.data;
  },

  /**
   * Revoke a role from a user
   */
  revokeRole: async (userId: number, roleId: number): Promise<void> => {
    await api.delete(`/api/roles/revoke/${userId}/${roleId}`);
  },

  /**
   * Get users with their roles (paginated, searchable, filterable)
   */
  getUsersWithRoles: async (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    role_filter?: string;
  }): Promise<UsersWithRolesResponse> => {
    const response = await api.get('/api/roles/admin/users-with-roles', { params });
    return response.data;
  },

  /**
   * Get all available permissions
   */
  getPermissions: async (): Promise<Permission[]> => {
    const response = await api.get('/api/roles/admin/permissions');
    return response.data;
  },

  /**
   * Get role analytics (distribution, user counts, recent changes)
   */
  getRoleAnalytics: async (): Promise<RoleAnalytics> => {
    const response = await api.get('/api/roles/admin/role-analytics');
    return response.data;
  },

  /**
   * Set user roles (assign or revoke a specific role)
   * Issue #981 - Add missing wrapper for PUT /admin/users/{user_id}/roles
   */
  setUserRoles: async (userId: number, data: SetUserRolesRequest): Promise<SetUserRolesResponse> => {
    const response = await api.put(`/admin/users/${userId}/roles`, data);
    return response.data;
  },

  // ============================================================================
  // Fair Following (Issue #1313)
  // ============================================================================

  /**
   * Follow a fair
   * Issue #1313 - Fair following feature
   * @param fairId - Fair ID to follow
   * @returns Object with follower_count and is_following status
   */
  followFair: async (fairId: number): Promise<{ follower_count: number; is_following: boolean; message: string }> => {
    const response = await api.post(`/api/fairs/${fairId}/follow`);
    return response.data;
  },

  /**
   * Unfollow a fair
   * Issue #1313 - Fair following feature
   * @param fairId - Fair ID to unfollow
   * @returns Object with follower_count and is_following status
   */
  unfollowFair: async (fairId: number): Promise<{ follower_count: number; is_following: boolean; message: string }> => {
    const response = await api.delete(`/api/fairs/${fairId}/follow`);
    return response.data;
  },

  /**
   * Get follower count for a fair (public endpoint)
   * Issue #1313 - Fair following feature
   * @param fairId - Fair ID
   * @returns Follower count
   */
  getFairFollowerCount: async (fairId: number): Promise<number> => {
    const response = await api.get(`/api/fairs/${fairId}/followers/count`);
    return response.data.follower_count;
  },

  /**
   * Check if current user is following a fair
   * Issue #1313 - Fair following feature
   * @param fairId - Fair ID
   * @returns True if following, false otherwise
   */
  isFollowingFair: async (fairId: number): Promise<boolean> => {
    const response = await api.get(`/api/fairs/${fairId}/followers/is-following`);
    return response.data.is_following;
  },

  /**
   * Get all fairs that current user is following
   * Issue #1313 - Fair following feature
   * @returns Array of fairs with follower_count and is_following fields
   */
  getUserFollowedFairs: async (): Promise<Fair[]> => {
    const response = await api.get('/api/users/me/following');
    return response.data;
  },

  // ==========================================================================
  // Blog API - Issue #1386
  // ==========================================================================

  /**
   * Create a blog category (admin only)
   * Issue #1386 - Blog platform
   * @param data - Category data
   * @returns Created category
   */
  createBlogCategory: async (data: BlogCategoryCreate): Promise<BlogCategory> => {
    const response = await api.post('/api/blog/categories', data);
    return response.data;
  },

  /**
   * Get all blog categories (public)
   * Issue #1386 - Blog platform
   * @returns Array of categories
   */
  getBlogCategories: async (): Promise<BlogCategory[]> => {
    const response = await api.get('/api/blog/categories');
    return response.data;
  },

  /**
   * Get a single blog category (public)
   * Issue #1386 - Blog platform
   * @param categoryId - Category ID
   * @returns Category details
   */
  getBlogCategory: async (categoryId: number): Promise<BlogCategory> => {
    const response = await api.get(`/api/blog/categories/${categoryId}`);
    return response.data;
  },

  /**
   * Create a blog tag (admin only)
   * Issue #1386 - Blog platform
   * @param data - Tag data
   * @returns Created tag
   */
  createBlogTag: async (data: BlogTagCreate): Promise<BlogTag> => {
    const response = await api.post('/api/blog/tags', data);
    return response.data;
  },

  /**
   * Get all blog tags (public)
   * Issue #1386 - Blog platform
   * @returns Array of tags
   */
  getBlogTags: async (): Promise<BlogTag[]> => {
    const response = await api.get('/api/blog/tags');
    return response.data;
  },

  /**
   * Create a blog post (admin only)
   * Issue #1386 - Blog platform
   * @param data - Post data
   * @returns Created post
   */
  createBlogPost: async (data: BlogPostCreate): Promise<BlogPost> => {
    const response = await api.post('/api/blog/posts', data);
    return response.data;
  },

  /**
   * Get blog posts with filters and pagination (public)
   * Issue #1386 - Blog platform
   * @param params - Query parameters
   * @returns Paginated post list with total count
   */
  getBlogPosts: async (params?: BlogPostListParams): Promise<{ posts: BlogPost[]; total: number; page: number; page_size: number }> => {
    const response = await api.get('/api/blog/posts', { params });
    return response.data;
  },

  /**
   * Get a single blog post by slug (public, increments view count)
   * Issue #1386 - Blog platform
   * @param slug - Post slug
   * @returns Post details with categories, tags, and author
   */
  getBlogPost: async (slug: string): Promise<BlogPost> => {
    const response = await api.get(`/api/blog/posts/${slug}`);
    return response.data;
  },

  /**
   * Update a blog post (admin only)
   * Issue #1386 - Blog platform
   * @param postId - Post ID
   * @param data - Updated post data
   * @returns Updated post
   */
  updateBlogPost: async (postId: number, data: BlogPostUpdate): Promise<BlogPost> => {
    const response = await api.put(`/api/blog/posts/${postId}`, data);
    return response.data;
  },

  /**
   * Delete a blog post (admin only)
   * Issue #1386 - Blog platform
   * @param postId - Post ID
   * @returns Success message
   */
  deleteBlogPost: async (postId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/blog/posts/${postId}`);
    return response.data;
  },

  /**
   * Add a comment to a blog post (public or authenticated)
   * Issue #1386 - Blog platform
   * Authenticated users' comments are auto-approved
   * @param postId - Post ID
   * @param data - Comment data
   * @returns Created comment
   */
  createBlogComment: async (postId: number, data: BlogCommentCreate): Promise<BlogComment> => {
    const response = await api.post(`/api/blog/posts/${postId}/comments`, data);
    return response.data;
  },

  /**
   * Get comments for a blog post (public sees approved only, admin sees all)
   * Issue #1386 - Blog platform
   * @param postId - Post ID
   * @returns Array of comments
   */
  getBlogComments: async (postId: number): Promise<BlogComment[]> => {
    const response = await api.get(`/api/blog/posts/${postId}/comments`);
    return response.data;
  },

  /**
   * Approve a blog comment (admin only)
   * Issue #1386 - Blog platform
   * @param commentId - Comment ID
   * @returns Updated comment
   */
  approveBlogComment: async (commentId: number): Promise<BlogComment> => {
    const response = await api.put(`/api/blog/comments/${commentId}/approve`);
    return response.data;
  },

  /**
   * Delete a blog comment (admin only)
   * Issue #1386 - Blog platform
   * @param commentId - Comment ID
   * @returns Success message
   */
  deleteBlogComment: async (commentId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/blog/comments/${commentId}`);
    return response.data;
  },

  /**
   * Get pending comments for moderation (admin only)
   * Issue #1386 - Blog platform
   * @returns Array of pending comments
   */
  getPendingBlogComments: async (): Promise<BlogComment[]> => {
    const response = await api.get('/api/blog/comments/pending');
    return response.data;
  },

  // ============================================================================
  // Fair Manager Onboarding API Methods (Issue #1455)
  // ============================================================================

  /**
   * Get onboarding status by token (public endpoint)
   * @param token - Onboarding token from approval email
   * @returns Onboarding progress, fair details, and application info
   */
  getOnboardingStatus: async (token: string): Promise<OnboardingStatusResponse> => {
    const response = await api.get(`/api/onboarding/${token}`);
    return response.data;
  },

  /**
   * Update onboarding step progress
   * @param token - Onboarding token
   * @param step - Step name ('welcome', 'tos', 'profile', 'contact', 'payment', 'fair_review', 'done')
   * @param data - Step-specific data to save
   * @returns Updated progress summary
   */
  updateOnboardingStep: async (token: string, step: string, data?: Record<string, unknown>): Promise<{ success: boolean; current_step: string; progress: OnboardingProgressSummary }> => {
    const response = await api.post(`/api/onboarding/${token}/step`, { step, data });
    return response.data;
  },

  /**
   * Accept Terms of Service during onboarding
   * @param token - Onboarding token
   * @returns Success confirmation with ToS acceptance timestamp
   */
  acceptOnboardingToS: async (token: string): Promise<{ success: boolean; message: string; tos_accepted: boolean; tos_accepted_at: string | null }> => {
    const response = await api.post(`/api/onboarding/${token}/tos`);
    return response.data;
  },

  /**
   * Complete onboarding flow
   * @param token - Onboarding token
   * @returns Success confirmation with dashboard URL
   */
  completeOnboarding: async (token: string): Promise<{ success: boolean; message: string; completed_at: string | null; fair_manager_dashboard_url: string }> => {
    const response = await api.post(`/api/onboarding/${token}/complete`);
    return response.data;
  },

  /**
   * Get onboarding status for an application (admin only)
   * @param applicationId - Fair manager application ID
   * @returns Onboarding status summary
   */
  getApplicationOnboardingStatus: async (applicationId: number): Promise<{ has_onboarding: boolean; onboarding_id?: number; current_step?: string; completed?: boolean; completed_at?: string | null; progress?: OnboardingProgressSummary; token_expires_at?: string | null; message?: string }> => {
    const response = await api.get(`/admin/applications/${applicationId}/onboarding-status`);
    return response.data;
  },
};

// Re-export pre-order status types for type-safe filtering (Issue #951)
export type { PreOrderStatus, PreOrderPaymentStatus };

export default api;